// Advent Calendar module - Data will be loaded from JSON file via Liquid templating
// Access data via: ADVENT_DATA variable (loaded at script initialization)

// Data is injected by Liquid templating in the section file

/* ============================================================================
   VIDEO IMPLEMENTATION OVERVIEW
   ============================================================================
   
   This codebase uses THREE different video implementations, each optimized
   for its specific use case. Understanding these differences is critical for
   maintenance, as this code is touched infrequently (every 2-3 years).
   
   ----------------------------------------------------------------------------
   1. COMING SOON VIDEO (Background Video)
   ----------------------------------------------------------------------------
   Type: Native HTML5 <video> element
   Purpose: Decorative background that always plays and loops
   Behavior: Autoplay, loop, muted, always visible (no user interaction needed)
   
   Technical Details:
   - Uses HTML5 <video> tag with autoplay, muted, loop, playsinline attributes
   - No JavaScript control needed (browser handles playback automatically)
   - No IntersectionObserver (always plays, no visibility detection)
   - No Vimeo.Player API (not a Vimeo embed)
   - Direct MP4 URL from schema setting: coming_soon_video_mp4_url from cdn
   - The Vimeo API does not work well with 2 autoplays on a page, even with a background=1 video
   
   Files:
   - Template: sections/advent-calendar-daily.liquid (lines ~155-220)
   - Styles: assets/advent-calendar-daily.css (lines ~1041-1196)
   - JavaScript: NONE (no JS needed for this video)
   
   Maintenance:
   - Edit Liquid template only (change MP4 URL in schema)
   - Edit CSS if styling needs change
   - NO JavaScript changes needed
   
   ----------------------------------------------------------------------------
   2. START VIDEO (Interactive Video)
   ----------------------------------------------------------------------------
   Type: Vimeo iframe embed
   Purpose: Explains how advent calendar works (linked from QR code)
   Behavior: Plays when section becomes visible, pauses when scrolled away
   
   Technical Details:
   - Uses Vimeo iframe embed with Vimeo.Player API for programmatic control
   - IntersectionObserver watches section visibility (50% threshold)
   - Plays when section is 50% visible (intersection ratio >= 0.5)
   - Pauses when section is not visible (intersection ratio < 0.5)
   - Vimeo video ID and hash from schema: start_video_id, start_video_hash
   
   Files:
   - Template: sections/advent-calendar-daily.liquid (lines ~203-260)
   - Styles: assets/advent-calendar-daily.css (lines ~1095-1290)
   - JavaScript: assets/advent-calendar-daily.js
     * initializeVideos() - creates Vimeo.Player instance (line ~773)
     * setupVideoIntersectionObserver() - handles play/pause (line ~860)
   
   Maintenance:
   - Edit Liquid template (change video ID/hash in schema)
   - Edit CSS if styling needs change
   - Edit initializeVideos() if initialization logic changes
   - Edit setupVideoIntersectionObserver() if visibility logic changes
   
   ----------------------------------------------------------------------------
   3. DAILY REVEAL VIDEO (Dynamic Interactive Video)
   ----------------------------------------------------------------------------
   Type: Vimeo iframe embed (dynamically loaded)
   Purpose: Day-specific video content (changes when day is selected)
   Behavior: Plays when section becomes visible, pauses when scrolled away
   
   Technical Details:
   - Uses Vimeo iframe embed with Vimeo.Player API for programmatic control
   - Video source changes dynamically when user selects different day
   - IntersectionObserver watches section visibility (50% threshold)
   - Plays when section is 50% visible (intersection ratio >= 0.5)
   - Pauses when section is not visible (intersection ratio < 0.5)
   - Video ID comes from ADVENT_DATA JSON (loaded per day)
   
   Files:
   - Template: sections/advent-calendar-daily.liquid (lines ~264-284)
   - Styles: assets/advent-calendar-daily.css (uses .daily-video-wrapper styles)
   - JavaScript: assets/advent-calendar-daily.js
     * setDailyRevealVideoSimple() / setDailyRevealVideoAdvanced() - creates Vimeo.Player instance dynamically (line ~1512)
     * setupVideoIntersectionObserver() - handles play/pause (line ~860)
     * updateDailyVideo() - called when day is selected (line ~1045)
   
   Maintenance:
   - Edit Liquid template (iframe structure)
   - Edit CSS if styling needs change
   - Edit setDailyRevealVideoSimple() / setDailyRevealVideoAdvanced() if video loading logic changes
   - Edit setupVideoIntersectionObserver() if visibility logic changes
   - Edit updateDailyVideo() if day selection logic changes
   
   ----------------------------------------------------------------------------
   WHY THREE DIFFERENT APPROACHES?
   ----------------------------------------------------------------------------
   
   Coming Soon: Simple background video, no interactivity needed, better
                performance with HTML5 video, avoids autoplay conflicts
   
   Start Video: Interactive but static (same video always), needs Vimeo features
                (analytics, controls), benefits from IntersectionObserver to
                prevent multiple videos playing simultaneously
   
   Daily Reveal: Interactive and dynamic (changes per day), needs Vimeo API for
                 dynamic loading, benefits from IntersectionObserver to prevent
                 multiple videos playing simultaneously
   
   ----------------------------------------------------------------------------
   QUICK REFERENCE FOR MAINTENANCE
   ----------------------------------------------------------------------------
   
   To change Coming Soon video:
   1. Update schema setting: coming_soon_video_mp4_url
   2. Done! (No JS changes needed)
   
   To change Start Video:
   1. Update schema settings: start_video_id, start_video_hash
   2. Check initializeVideos() if needed
   3. Check setupVideoIntersectionObserver() if needed
   
   To change Daily Reveal video behavior:
   1. Check setDailyRevealVideoSimple() / setDailyRevealVideoAdvanced() for video loading
   2. Check setupVideoIntersectionObserver() for visibility logic
   3. Check updateDailyVideo() for day selection logic
   
   ============================================================================ */

// Module-level constant for advent calendar data (parsed once)
// This is a Shopify Meta Object that is loaded within a script tag in the liquid file and interpreted
let ADVENT_DATA = null;//loaded with domloaded

// Configuration
const ADVENT_YEAR = 2026; // current year
const LAST_ADVENT_DAY = 25; // limit of advent calendar
const MOBILE_MIDDLE_DAY = 12; // day that ends first row on mobile, needs special styling

// Video Player Logic - Advanced vs Simple
// 
// The advanced video player uses .src initially and then if it fails, loads the video via the Vimeo API.
// The simple method has no fallback and just loads the video via the Vimeo API with only .src.
// Changing the .src of an iframe is not a good idea and this might get deprecated/blocked 
// by browsers at some point. Gut it has the best behavior where the autoplay starts as 
// expected and there is no flash when the browser blocks an autoplay programmatically and 
// we have to override that with BROWSER_VIDEO_LOADING_WORKAROUND.  
// So to be robust, we try the .src method first and then fall back to the loadVideo() method.
// The simple method is what we used in 2024 and it worked so if this has any issues,
// we can revert easily without a code change by just updating this variable.
// In 2026, if the advanced video player works, just switch everything to using the advanced video player.
const USE_ADVANCED_VIDEO_PLAYER = true; 

// WORKAROUND: Browser video autoplay timing issue
// 
// When switching back to day 0, the start video section is shown (display: block),
// but browsers may automatically pause the video shortly after it becomes visible.
// This delay gives the browser time to complete its autoplay pause behavior before
// we manually trigger playback. Without this delay, the manual play() call may
// happen before the browser pauses it, resulting in the video remaining paused.
// 
// Timing breakdown:
// - 0ms: display: block is set, section becomes visible
// - ~0-100ms: IntersectionObserver detects visibility and tries to play
// - ~100-400ms: Browser's autoplay policy may pause the video
// - 500ms: Our manual play() call executes, successfully starting playback
//
// This is a workaround for browser autoplay restrictions and IntersectionObserver
// timing issues when elements change from display: none to display: block.
// Value: 500ms (tested and confirmed to work reliably across browsers)
// if there are issues, then increase this value.
const BROWSER_VIDEO_LOADING_WORKAROUND = 500; // milliseconds

// Global state
let g_LastSelection = null;//this will show the intro screen by default (day 0)

// Timeout tracking for feedback form initialization (debounce rapid day changes)
let g_feedbackInitTimeout = null;
let g_feedbackFormRetryTimeout = null;

// fields for handling the syncing of the forms
// with the backend Zapier database.
// this tracks what information needs to be sent
// to Zapier to make it easier to look through logs later
// to see feedback. only the information that has changed is sent.
const FEEDBACK_STATUS = {
  NO_CHANGE: 'nochange',
  CREATED: 'created',
  MODIFIED: 'modified',
};

let g_feedbackSubmitButton = null;
let g_feedbackSubmitButtonState = {
  currentDay: null,
  isSubmitting: false,
  successTimeoutId: null,
  errorTimeoutId: null,
  errorRetryTimeoutId: null,
};

// Store button click handlers to allow removal before re-adding
const g_feedbackButtonHandlers = new WeakMap();

// Store submit button click handler to allow removal before re-adding
let g_feedbackSubmitButtonHandler = null;

/**
 * Detects if browser is Safari on desktop (not mobile)
 * Returns true only for Safari desktop browsers
 */
function isSafariDesktop() {
  const userAgent = navigator.userAgent;
  const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent);
  const isMobile = /Mobi|Android|iPhone|iPad|iPod/i.test(userAgent);
  return isSafari && !isMobile;
}

/**
 * Adds overlay shield to daily video wrapper
 * Called when switching days or when video section scrolls back into view
 */
function addDailyVideoOverlay() {
  if (!isSafariDesktop()) return; // Only for Safari desktop
  
  const wrapper = document.querySelector('.daily-video-wrapper');
  if (!wrapper) return;
  
  // Remove is-active class to show overlay
  // Simple guard: only remove if not already removed
  if (wrapper.classList.contains('is-active')) {
    wrapper.classList.remove('is-active');
  }
}

/**
 * Removes overlay shield (activates video interaction)
 * Called when user clicks the overlay
 */
function removeDailyVideoOverlay() {
  const wrapper = document.querySelector('.daily-video-wrapper');
  if (!wrapper) return;
  
  // Add is-active class to hide overlay
  // Simple guard: only add if not already added
  if (!wrapper.classList.contains('is-active')) {
    wrapper.classList.add('is-active');
  }
}

/**
 * Sets up click handler on overlay shield
 * Should be called after overlay is added
 */
function setupDailyVideoOverlayClickHandler() {
  if (!isSafariDesktop()) return; // Only for Safari desktop
  
  const overlay = document.querySelector('.daily-video-overlay-shield');
  if (!overlay) return;
  
  // Remove existing listener if any (prevent duplicates)
  const newHandler = function(e) {
    e.preventDefault();
    e.stopPropagation();
    removeDailyVideoOverlay();
    // Optionally trigger play if video is ready
    const player = g_videoPlayers['daily-video-iframe'];
    if (player) {
      player.ready().then(() => {
        return player.play();
      }).catch(error => {
        // Ignore errors - video may not be ready yet
        console.log('Could not play video after overlay click:', error.message);
      });
    }
  };
  
  // Remove old listener and add new one
  overlay.removeEventListener('click', overlay._clickHandler);
  overlay._clickHandler = newHandler;
  overlay.addEventListener('click', newHandler);
}

// Video initialization state
// Tracks whether Vimeo players for Start Video section have been initialized
// Prevents double initialization when initializeVideos() is called from multiple sources
// (DOMContentLoaded event or script onload callback)
// Note: Coming Soon video uses HTML5 <video> element, not Vimeo, so it doesn't need initialization
let g_videosInitialized = false;

// Video player instances storage
// Maps iframe IDs to Vimeo.Player instances for use by IntersectionObserver
// Structure: { 'start-video-iframe': player, 'daily-video-iframe': player }
// Note: Coming Soon video uses HTML5 <video> element, not Vimeo iframe, so no player instance needed
const g_videoPlayers = {};

// Global variable to remember user's mute preference for daily video
// Defaults to true (muted) to match initial video state
let g_dailyVideoMuted = true;

// Vimeo API readiness Promise system
// Provides centralized way to wait for Vimeo Player API to be available
// Resolved when window.Vimeo is available, rejected if loading fails
// This prevents race conditions where code tries to use Vimeo.Player before the script loads
let g_vimeoApiReadyResolve = null;
let g_vimeoApiReadyReject = null;
const g_vimeoApiReady = new Promise((resolve, reject) => {
  g_vimeoApiReadyResolve = resolve;
  g_vimeoApiReadyReject = reject;
  
  // If Vimeo is already loaded (e.g., from previous page load or another script),
  // resolve immediately
  if (window.Vimeo && window.Vimeo.Player) {
    resolve();
  }
});

// Helper function to wait for Vimeo API to be ready
// Returns a promise that resolves when window.Vimeo is available
// Includes timeout to prevent infinite waiting (default: 10 seconds)
// Usage: await waitForVimeoAPI() or waitForVimeoAPI().then(() => { ... })
function waitForVimeoAPI(timeoutMs = 10000) {
  // If already available, return resolved promise immediately
  if (window.Vimeo && window.Vimeo.Player) {
    return Promise.resolve();
  }
  
  // Create timeout promise that rejects after timeout
  const timeoutPromise = new Promise((_, reject) => {
    setTimeout(() => {
      reject(new Error(`Vimeo API not available after ${timeoutMs}ms timeout`));
    }, timeoutMs);
  });
  
  // Race between API ready promise and timeout
  return Promise.race([
    g_vimeoApiReady,
    timeoutPromise
  ]).catch(error => {
    console.warn('Vimeo API readiness check failed:', error.message);
    throw error;
  });
}

window.ADVENT_DEBUGGING = false;//can be overriden with debugging=true url parameter

// Function to move day selector to header
function moveDaySelectorToHeader() {
  const daySelectorTemp = document.getElementById('day-selector-temp');
  const header = document.getElementById('header');
  
  if (daySelectorTemp && header) {
    // Move the day selector content to the header
    const daySelectorContent = daySelectorTemp.querySelector('.day-selector-outer-wrapper');
    if (daySelectorContent) {
      header.appendChild(daySelectorContent);
      // Remove the temporary container
      daySelectorTemp.remove();
    }
  }
}


// Initialize everything when DOM is ready
document.addEventListener('DOMContentLoaded', function() {
  console.log('🚀 Initializing Advent Calendar...');

  // Add Safari desktop class to body for CSS targeting
  if (isSafariDesktop()) {
    document.body.classList.add('is-safari-desktop');
    console.log('Safari desktop detected - overlay shield enabled');
  }

  // 1. Move day selector to header
  moveDaySelectorToHeader();

  // 2. Initialize video positioning (but not content yet)
  //updateVideoPosition();
  updateOverlayImageWidth();

  // 3. Initialize data when DOM is ready
  const el = document.getElementById("advent-data");
  if (!el) {
    console.log('Could not locate advent-data object');
    return;
  }
    
  try {
    ADVENT_DATA = JSON.parse(el.textContent);

    if (!ADVENT_DATA) {
      console.error('Advent calendar data is empty');
      return;
    }
    
    console.log('Advent calendar data loaded successfully');
  } catch (error) {
    console.error('Error parsing advent calendar data:', error);
    return;
  }
    
  if (!ADVENT_DATA || Object.keys(ADVENT_DATA).length === 0) {
    console.error("AdventCalendar data is not defined or empty.");
    return;
  }
    

  startSnowAnimation(); /* show snowflakes */
    
  // 4. Write UI components
  writeDateSelectorUI(); // do this once - this is for the date selector
  
  updateDailyRevealUI(); // update daily reveal UI, repeat as needed

  // 5. Update daily video content (after data is loaded and UI is written)
  updateDailyVideo();

  // 6. Show preorder section if applicable
  // don't show when day 0 is selected
  updatePreorderSection();

  // 7. Initialize animations and observers
  initializeStarAnimation();

  // 8. Initialize Vimeo videos, connect them with the Vimeo JS loaded
  initializeVideos();
  
  // 9. Setup IntersectionObserver for video sections (play/pause based on visibility)
  // This must be called after initializeVideos() so that player instances are available
  setupVideoIntersectionObserver();
  
  // 9a. Setup IntersectionObserver for celestial section video
  setupCelestialVideoObserver();
  
  // 9b. Setup overlay for daily video if already visible on page load (Safari desktop)
  if (isSafariDesktop()) {
    // Check if daily video section is visible
    const dailyVideoSection = document.getElementById('daily-video-section');
    if (dailyVideoSection) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && entry.intersectionRatio >= 0.5) {
            addDailyVideoOverlay();
            setupDailyVideoOverlayClickHandler();
          }
        });
      }, { threshold: 0.5 });
      observer.observe(dailyVideoSection);
    }
  }

  // 10. Set up resize event listeners
  window.addEventListener('resize', function() {
    updateOverlayImageWidth();
    updateMapStar(); // Recalculate star position on resize
  });

  // 9. Write feedback form validation
  writeFeedbackForm();

  console.log('✅ Advent Calendar initialization complete');
});

// Writes the date selector buttons that can be clicked on
function writeDateSelectorUI() {

  const selectedDay = resolveDay(); // day to show on the calendar, usually today's day, the last selection, the spotlight email that someone clicked on
  const maxDay = getMaxDay(); // maximum accessible day (0-25) - determines which days are active/red in selector

  let dateSelectorHTML = ""; // used in $('#daily_calendar_day_selectors')
  // intro day = day 0
  dateSelectorHTML += `<div role="button" aria-label="Day 0" title="0" class="daily_calendar_day_selector active"><p>☆</p></div>`;

  // all previous advent dates
  for (let i = 1; i <= maxDay; i++) {
    dateSelectorHTML += `<div role="button" aria-label="Day ${i}" title="${i}" class="daily_calendar_day_selector active"><p>${i}</p></div>`;
  }
    
  // future, not selectable
  for (let j = maxDay + 1; j <= LAST_ADVENT_DAY; j++) {
    dateSelectorHTML += '<div title="' + j + '" class="daily_calendar_day_selector"><p>' + j + '</p></div>';
  }
    
  // append to DOM
  const dateSelectorContainer = document.getElementById('daily_calendar_day_selectors');
  
  if (dateSelectorContainer) dateSelectorContainer.innerHTML = dateSelectorHTML;
    
  // Add maxday class (arrow indicator) to the maximum accessible day
  // This indicates which day is the "today" or maximum accessible day, not the selected day
  if (maxDay >= 0) {
    const maxDayObject = document.querySelector(`.daily_calendar_day_selector[title="${maxDay}"]`);
    if (maxDayObject) {
      maxDayObject.classList.add("maxday");
    }
  }
  
  // Add selected class to the currently selected day (may be different from maxDay)
  // This indicates which day the user has selected or which day should be displayed
  if (selectedDay >= 0) {
    const selectedDayObject = document.querySelector(`.daily_calendar_day_selector[title="${selectedDay}"]`);
    if (selectedDayObject) {
      selectedDayObject.classList.add("selected"); // Ensure selected state is visible on initial load
    }
  }
  
  // Add special class for LAST_ADVENT_DAY to make entire bar black, otherwise a litte bit of gray peeks out
  // Check if current max accessible day is December 25th (LAST_ADVENT_DAY), not just if day 25 is selected
  if (maxDay == LAST_ADVENT_DAY) {
    if (dateSelectorContainer) {
      dateSelectorContainer.classList.add('last-in-row');//adds arrow tip
    }
  }
  
  // Add special class for MOBILE_MIDDLE_DAY to prevent gray background from peeking out on mobile
  if (selectedDay == MOBILE_MIDDLE_DAY) {
    if (dateSelectorContainer) {
      dateSelectorContainer.classList.add('mobile-last-in-row');//adds arrow tip
    }
  }
    
  // connect date selector buttons with functionality
  // this global click event listener watches for clickes anywhere on the page
  // when a click occurs, the handler checks if the clicked element is an active
  // date selector button. If it is, the handler calls onButtonClick with the button number.
  document.addEventListener('click', function(e) {
    if (e.target.closest('.daily_calendar_day_selector.active')) {
      const buttonNumber = e.target.closest('.daily_calendar_day_selector').getAttribute('title');
      console.log("selected dayID = " + buttonNumber);
      onButtonClick(buttonNumber);
    }
  });
}

// Updates the day selector container with special styling classes based on selected day
function updateDaySelector(daily_reveal_id) {
  const daySelectorContainer = document.getElementById('daily_calendar_day_selectors');
  if (!daySelectorContainer) return;
  
  const selectedDay = parseInt(daily_reveal_id, 10);
  // Validate day ID to prevent NaN or out-of-range values from breaking comparisons
  if (isNaN(selectedDay) || selectedDay < 0 || selectedDay > LAST_ADVENT_DAY) {
    console.error('Invalid day ID in updateDaySelector:', daily_reveal_id);
    return;
  }
  
  // Handle LAST_ADVENT_DAY special styling, add special class for LAST_ADVENT_DAY to make entire bar black, otherwise a litte bit of gray peeks out
  // Check if current max accessible day is December 25th (LAST_ADVENT_DAY), not just if day 25 is selected
  const currentMaxDay = getMaxDay();
  if (currentMaxDay == LAST_ADVENT_DAY) {
    daySelectorContainer.classList.add('last-in-row');//put little arrow pointer on the last day
  } else {
    daySelectorContainer.classList.remove('last-in-row');
  }
  
  // Handle MOBILE_MIDDLE_DAY special styling to prevent gray background from peeking out on mobile
  if (selectedDay == MOBILE_MIDDLE_DAY) {
    daySelectorContainer.classList.add('mobile-last-in-row');//put little arrow pointer on the last day in the first row
  } else {
    daySelectorContainer.classList.remove('mobile-last-in-row');//put little arrow pointer on the last day in second row
  }
}

// Determines whether the preorder section should be shown based on configured day
// Called once on page load and when day changes
function updatePreorderSection() {
  const preorderSection = document.getElementById('preorder-section');
  if (!preorderSection) return;

  // Use g_LastSelection if set (user's explicit choice), otherwise fall back to resolveDay()
  // This ensures user clicks are respected even when emergency day is set
  // resolveDay() checks emergency day before g_LastSelection, so we need to prioritize user selection here
  const selectedDay = getCurrentDay();

  // If day 0 is selected, always hide preorder section (intro/start state)
  if (selectedDay == 0) {
    preorderSection.style.display = 'none';
    return;
  }

  const fallbackPreorderDay = 22; // default day, matches schema default
  const preorderDayAttr = preorderSection.getAttribute('data-preorder-day');
  const preorderDay = parseInt(preorderDayAttr, 10);
  const startPreorderDay = isNaN(preorderDay) ? fallbackPreorderDay : preorderDay;

  // Show preorder section if max accessible day has reached the start preorder day
  if (getMaxDay() >= startPreorderDay) {
    preorderSection.style.display = 'flex';
  } else {
    preorderSection.style.display = 'none';
  }
}

function onButtonClick(daily_reveal_id) {
  // Validate day ID before processing to prevent invalid state
  const dayId = parseInt(daily_reveal_id, 10);
  if (isNaN(dayId) || dayId < 0 || dayId > LAST_ADVENT_DAY) {
    console.log('Invalid day ID in onButtonClick:', daily_reveal_id);
    return;
  }
  
  // Save any pending form state for the current day before switching
  // This ensures no form data is lost when user switches dates
  saveCurrentFormStateBeforeSwitch();
  
  // record the last day clicked for the UI
  g_LastSelection = dayId;
  
  // Update day selector styling
  updateDaySelector(daily_reveal_id);
  
  // Update daily video -- collab video -- for selected day
  updateDailyVideo();
  
  updateDailyRevealUI(); // update the UI with the new date's information
  loadSavedFormState();
}

// Main entry point for updating the daily reveal UI
// Orchestrates which sections to show/hide and routes to appropriate content population
function updateDailyRevealUI() {
  const daily_reveal_data = ADVENT_DATA;

  if (!daily_reveal_data) {
    console.log('daily_reveal updateDailyRevealUI exit');
    return;
  }
    
  // Use g_LastSelection if set (user's explicit choice), otherwise fall back to resolveDay()
  // This ensures user clicks are respected even when emergency day is set
  // resolveDay() checks emergency day before g_LastSelection, so we need to prioritize user selection here
  const selectedDay = getCurrentDay();
  
  if (selectedDay == 0) {
    hideDailyRevealSections();
    showDayZeroSections(); // Explicitly show start video section
  } else {
    hideDayZeroSections(); // Hide start video when switching away from day 0
    populateDailyRevealContent(selectedDay); // populate day-specific content
  }
  
  // Update preorder section visibility based on selected day
  // Update explore more section visibility based on selected day
  // updatePreorderSection();
  // updateExploreMoreSection(selectedDay);
  
  // Update active states - unselect previously selected items
  const activeDaySelectors = document.querySelectorAll('.daily_calendar_day_selector.selected');
  activeDaySelectors.forEach(selector => selector.classList.remove("selected"));
  
  const selectedDaySelector = document.querySelector(`.daily_calendar_day_selector[title="${selectedDay}"]`);
  if (selectedDaySelector) selectedDaySelector.classList.add("selected");
}


function getEmergencyDay() {
  const emergencyValue = window.EMERGENCY_DAILY_DAY;
  
  if (emergencyValue === null || emergencyValue === undefined || emergencyValue === '') {
    return null;
  }
  
  const parsedDay = parseInt(emergencyValue, 10);
  return isNaN(parsedDay) ? null : parsedDay;
}

// Returns the maximum accessible day as an integer (0-25)
// Priority: debug mode > emergency day > actual today
// Calculates days since November 30th, capped at LAST_ADVENT_DAY (25)
// Any day before December 1st returns 0
function getMaxDay() {
  // Calculate max accessible day based on priority: debug mode > emergency day > actual today
  let maxDay = 0;

  let dateToday = new Date();
  const adventStartDate = new Date(ADVENT_YEAR, 10, 30); // November 30th (zero-based month index)
  let daysSince = Math.max(0, Math.floor((dateToday - adventStartDate) / (1000 * 60 * 60 * 24)));// don't allow negative
  daysSince = Math.min(daysSince, LAST_ADVENT_DAY);//don't allow more than 25

  const urlDay = getUrlDay();
  const emergencyDay = getEmergencyDay();//returns null if no emergency day is set, otherwise returns the emergency day as an integer
  const isDebugging = isDebugMode();

  if (isDebugging) {
    maxDay = (urlDay != -1) ? urlDay : daysSince;
  } else if ((emergencyDay != null) && (emergencyDay >= 0)) {
    maxDay = emergencyDay; // for debugging, december 25th
  } else if ((daysSince != null) && (daysSince >= 0)) {
    maxDay = daysSince;
  }

  return maxDay;
}

// Looks at UI, last selection, Debug Mode, Emergency Day, URL parameters, and current date to determine what to display
// PRIORITY IS:
// 1. Debug mode + URL parameter (?debugging=true&day=##) - highest priority, allows testing even when emergency day is set
// 2. URL parameter (?day=##) - only if URL day < emergency day (if emergency day is set), otherwise always checked
// 3. Emergency day override (if set in schema) - overrides all user local time settings
// 4. UI selection (button click, i.e. g_LastSelection)
// 5. URL parameter (if no emergency day, or if URL day >= emergency day) - capped to current advent day
// 6. Current max accessible day (calculated from max day logic, which reflects debug mode via getMaxDay())
function resolveDay() {
  // Debug mode with URL parameter - highest priority (overrides emergency day and everything else)
  // This allows developers to test specific days even when emergency day override is active
  // Note: Debug mode is handled in getMaxDay(), which is called later in this function
  if (isDebugMode()) {
    const urlDay = getUrlDay(); // 0 - 25
    if (urlDay >= 0) {
      return Math.min(urlDay, LAST_ADVENT_DAY);
    }
  }
  
  // Get emergency day and URL day for conditional logic
  const emergencyDay = getEmergencyDay();
  const urlDay = getUrlDay(); // 0 - 25
  
  // URL parameter - second priority, but only if it's less than emergency day (if emergency day is set)
  // This allows email links to navigate to past days even when emergency day is active
  // If URL day >= emergency day, emergency day will take precedence below
  if (urlDay >= 0) {
    // If no emergency day is set, or URL day is less than emergency day, use URL day
    if (emergencyDay == null || emergencyDay == undefined || emergencyDay == -1 || urlDay < emergencyDay) {
      const maxDay = getMaxDay(); // returns integer of max accessible day 0-25 (already reflects debug mode)
      const cappedUrlDay = Math.min(urlDay, maxDay); // cap to max accessible day (prevents future day access)
      return cappedUrlDay;
    }
    // If URL day >= emergency day, fall through to emergency day check below
  }
  
  // Emergency day override - third priority (overrides user selections and local time)
  // This allows admins to force a specific day regardless of user's local time or selections
  // Acts as a "minimum" day that cannot be bypassed by URL parameters for future days
  if (emergencyDay != null && emergencyDay != undefined && emergencyDay != -1) {
    return emergencyDay;
  }
  
  // UI selection (button click) - user's explicit choice
  if (g_LastSelection != null && g_LastSelection != undefined && g_LastSelection != -1) {
    return g_LastSelection;
  }
    
  const maxDay = getMaxDay(); // returns integer of max accessible day 0-25 (already reflects debug mode)
    
  // Final fallback: return current max accessible day
  // (URL parameter was already handled above if it existed and met conditions)
  return maxDay;
}

// Returns 0-25, -1 if no day found in url
function getUrlDay() {
  // if there is a param specified in the url (e.g. ?day=##) then switch to that date (assuming it's not beyond today)
  const params = getUrlParameters();
    
  if (params['day']) {
    const urlDay = parseInt(params['day']);
    return isNaN(urlDay) ? -1 : Math.min(urlDay, LAST_ADVENT_DAY);
  }
    
  return -1;//no day found in url
}

// go into debug state if appropriate parameters sre detected
// using ?day=## in first version (easier for debugging)
// using ?debugging=true in live version, also requires day=##
function isDebugMode() {
  const params = getUrlParameters();
  return (params['debugging'] === 'true');
}

// Used to determine if there is a day specified from the Klaviyo email urlParameters
// if user selects previous day from email, show that page
function getUrlParameters() {
  const params = new URLSearchParams(window.location.search);
  const obj = {};

  for (const key of params.keys()) {
    obj[key] = params.get(key);
  }

  return obj;
}

// Utility function
// Write UI Date and pretty format it
function writePrettyDay(adventDay /* 0 - 25 */) {
  let date = new Date(ADVENT_YEAR, 11 /* month is zero based, 11=December */, adventDay);

  const day = date.getDate(); 
  let month = date.getMonth();
  const dayOfWeek = date.getDay();
    
  const monthArr = ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"];
  month = monthArr[month];

  let formattedDay;
  if (day == 1 || day == 21) {
    formattedDay = day + '<sup>st</sup>';
  } else if (day == 2 || day == 22) {
    formattedDay = day + '<sup>nd</sup>';
  } else if (day == 3 || day == 23) {
    formattedDay = day + '<sup>rd</sup>';
  } else {
    formattedDay = day + '<sup>th</sup>';
  }
    
  return `${dayOfWeek}, ${formattedDay}`;
}

// Populates day-specific content from ADVENT_DATA for a given day (1-25)
// Called when user loads the page or selects a day button
function populateDailyRevealContent(day) {
  const daily_reveal = ADVENT_DATA[day];

  if (!daily_reveal) {
    console.error('No data found for day:', day);
    return;
  }

  // Show daily reveal sections
  showDailyRevealSections();

  // Update Day Overview section
  updateDayOverviewSection(day);
  
  // Update Map section
  updateMapSection(day);

  // Update Celestial Section
  updateCelestialSection(day);

  //write section where you can buy more treats if the link exists, otherwise hide the section
  updateFeedbackSection(day);
  
  // Write feedback form after content is written (debounce rapid day changes)
  if (g_feedbackInitTimeout) {
    clearTimeout(g_feedbackInitTimeout);
  }
  g_feedbackInitTimeout = setTimeout(function() {
    writeFeedbackToggleButtons();
    writeFeedbackForm();
    g_feedbackInitTimeout = null;
  }, 100);
}

// Function to update Day Overview section with real data
function updateDayOverviewSection(day) {
  if (!ADVENT_DATA || !ADVENT_DATA[day]) return;
  
  const dailyData = ADVENT_DATA[day];
  
  // Update Featured Confection
  updateElement('#day-overview-confection', dailyData.treat_name);
  
  // Update Featured Chocolatier
  updateElement('#day-overview-chocolatier', dailyData.collaborator_name);
  
  // Update Flavor Notes
  updateElement('#day-overview-flavor-notes', dailyData.collaborator_flavor_notes);
  
  // Update Notes from the Chocolatier
  updateElement('#day-overview-notes-text', dailyData.collaborator_note || 'No notes. Just enjoy!');
  
  // Update the "NOTES FROM THE CHOCOLATIER" header to include the chocolatier's name
  const chocolatierName = dailyData.chocolatier_name || 'THE CHOCOLATIER';
  updateElement('.day-overview-notes-header', `NOTES FROM ${chocolatierName.toUpperCase()}`);
  
  // Update Collaborator Portrait Image
  const portraitImage = document.querySelector('.collaborator-portrait-image');
  if (dailyData.collaborator_portrait) {
    // Show and update the image
    if (portraitImage) {
      portraitImage.style.display = 'block';
      portraitImage.src = dailyData.collaborator_portrait;
      portraitImage.alt = `${dailyData.collaborator_name || 'Collaborator'} Portrait`;
    }
  } else {
    // Hide the image if no portrait exists
    if (portraitImage) {
      portraitImage.style.display = 'none';
    }
  }
}

// Function to update Map section with real data
function updateMapSection(day) {
  if (!ADVENT_DATA || !ADVENT_DATA[day]) return;
  
  const dailyData = ADVENT_DATA[day];
  
  // Update collaborator name
  updateElement('#collaborator-name', dailyData.collaborator_name || 'Collaborator Name');
  
  // Update map star position
  // this is also called on resize so we set this function up to automatically grab the advent data key value 
  // when it is called so we don't need to pass in the location parameters
  updateMapStar();
  
  // Update location
  updateElement('#collaborator-location', dailyData.collaborator_location || 'Location');
  
  // Update started year
  updateElement('#collaborator-started', dailyData.collaborator_year || 'Year');
  
  // Update learn more link using helper function
  updateLink('.learn-more-link', dailyData.collaborator_url);
  
  // Update collaborator bio
  updateElement('#collaborator-bio', dailyData.collaborator_bio || 'Collaborator bio coming soon.');
}

// Function to update Celestial Section with real data
function updateCelestialSection(day) {
  if (!ADVENT_DATA || !ADVENT_DATA[day]) return;
  
  const dailyData = ADVENT_DATA[day];
  
  // Update celestial title
  updateElement('.celestial-title', dailyData.celestial_name || 'Celestial Event');
  
  // Update celestial description
  updateElement('.celestial-description', dailyData.celestial_note || 'Celestial event description coming soon.');
  
  // Update celestial video/image based on celestial_animation availability
  const celestialVideo = document.getElementById('celestial-video');
  const celestialVideoSource = celestialVideo?.querySelector('source');
  const celestialImg = document.querySelector('.celestial-image .celestial-img');
  const celestialImgMobile = document.querySelector('.celestial-image-mobile .celestial-img');
  const celestialSection = document.getElementById('celestial-section');
  
  // Update alt text/aria-label with celestial_name for accessibility
  const celestialName = dailyData.celestial_name || 'Celestial Event';
  if (celestialVideo) {
    celestialVideo.setAttribute('aria-label', celestialName);
  }
  if (celestialImgMobile) {
    celestialImgMobile.setAttribute('alt', celestialName);
    // Update mobile image source from ADVENT_DATA if on mobile and data exists
    if (isMobile() && dailyData.ornament_illustration_CDN) {
      celestialImgMobile.src = dailyData.ornament_illustration_CDN;
    }
  }
  
  if (celestialVideo && celestialVideoSource && celestialImg) {
    
    if (dailyData.celestial_animation) {
      // Video available: show video, hide image
      celestialVideoSource.src = dailyData.celestial_animation;
      celestialVideo.load(); // Reload video with new source
      celestialVideo.style.display = 'block';
      celestialImg.style.display = 'none';
      
      // If section is currently visible, play the video after it loads
      const playVideoIfVisible = () => {
        if (isSectionVisible(celestialSection)) {
          celestialVideo.play().then(() => {
            console.log('Celestial video playing (section visible after day change)');
          }).catch(error => {
            // Expected if browser blocks autoplay
            console.log('Could not play celestial video (autoplay may be blocked):', error.message);
          });
        }
      };
      
      // Wait for video to be ready before playing
      if (celestialVideo.readyState >= 3) { // HAVE_FUTURE_DATA or higher
        // Video is already loaded, play immediately
        playVideoIfVisible();
      } else {
        // Wait for video to load
        celestialVideo.addEventListener('canplay', playVideoIfVisible, { once: true });
      }
    } else {
      // No video: hide video, show image
      celestialVideo.style.display = 'none';
      celestialImg.style.display = 'block';
    }
  }
}

function updateFeedbackSection(day) {

  if (!ADVENT_DATA || !ADVENT_DATA[day]) return;
  
  const dailyData = ADVENT_DATA[day];
  
  // Update explore more section title with treat name
  updateElement('.explore-more-title', dailyData.treat_name || 'Discover More');
  
  // Update explore more button using helper function
  updateButton('.explore-more-button', dailyData.button_copy, dailyData.mini_page_link);
  
  // Update explore more image link using helper function
  updateImage('.daily-treat-img', dailyData.treat_CDN, dailyData.treat_name, dailyData.mini_page_link);

  updateElement('.explore-more-description', dailyData.treat_description || '');

  // Show/hide buttons based on mini_page_link & device type
  if (dailyData.mini_page_link){
    if (isMobile()) {
      updateExploreMoreButtonDisplays('.explore-more-button-container--mobile-only', true);
    } else {
      updateExploreMoreButtonDisplays('.explore-more-button-container', true);
    }
    updateLink('#explore-more-image-link', dailyData.mini_page_link);
    updateLink('#explore-more-button-desktop', dailyData.mini_page_link);
  } else {
    updateExploreMoreButtonDisplays('.explore-more-button-container', false);
    updateExploreMoreButtonDisplays('.explore-more-button-container--mobile-only', false);
  }
}

function isMobile() {
  return window.innerWidth < 768;
}

// Helper function to update text content
function updateElement(selector, content) {
  const elements = document.querySelectorAll(selector);
  elements.forEach(element => {
    if (element) {
      element.innerHTML = content;
    }
  });
}

function updateButton(selector, content, link) {
  const buttons = document.querySelectorAll(selector);
  buttons.forEach(button => {
    if (!button) return;
    
    // Update button text content (e.g. "Order More")
    if (content) {
      button.textContent = content;
    }
    
    // Update button link with proper URL handling
    if (link) {
      button.href = normalizeExternalUrl(link);
      button.style.display = 'inline-block';
    } else {
      button.style.display = 'none';
    }
  });
}

function updateLink(selector, link) {
  const links = document.querySelectorAll(selector);
  links.forEach(linkElement => {
    if (!linkElement) return;
    
    // Update link with proper URL handling
    if (link) {
      linkElement.href = normalizeExternalUrl(link);
    } else {
      linkElement.style.display = 'none';
    }
  });
}

/* updates the display property */
// we use this for the mobile / desktop "Order More buttons " where 
// there are two buttons displayed depending upon the device
// if we hide one, we want to only show it again if it's appropriate for the device
// so we are careful to set a display 'none' and then remove it
// so that we can revert to the css that auto-detects the device and displays
// the button correctly
// we only need this in the case that there is no link for ordering more treats
function updateExploreMoreButtonDisplays(selector, visibility /*true/false*/) {
  const elements = document.querySelectorAll(selector);

  if (!visibility) {
    elements.forEach(element => {
      if (element) {
        element.style.display = "none";
      }
    });
  } else /*assume visible/display/block*/ {
    elements.forEach(element => {
        if (element) {
          element.style.removeProperty("display");
        }
    });
  }
}

// Helper function to update image attributes
function updateImage(selector, src, alt, /* optional */ link) {
  const elements = document.querySelectorAll(selector);
  elements.forEach(element => {
    if (element) {
      element.src = src;
      element.alt = alt;
      
      // Update parent link if it exists and link is provided
      if (link && element.parentElement && element.parentElement.tagName === 'A') {
        const parentLink = element.parentElement;
        parentLink.href = normalizeExternalUrl(link);
        parentLink.style.display = 'inline-block';
        parentLink.style.pointerEvents = 'auto';
        parentLink.style.cursor = 'pointer';
        parentLink.removeAttribute('onclick');
      } else if (element.parentElement && element.parentElement.tagName === 'A') {
        // Disable parent link if no link provided (but keep image visible)
        const parentLink = element.parentElement;
        parentLink.href = 'javascript:void(0)';
        parentLink.style.pointerEvents = 'none';
        parentLink.style.cursor = 'default';
        parentLink.setAttribute('onclick', 'return false;');
      }
    }
  });
}


// Helper function to update map star position
function updateMapStar() {
  // Base star size constant
  const BASE_STAR_SIZE = 20; // Base star size in pixels - this is set in the css so don't go by image dimensions
  const BASE_STAR_CENTER_OFFSET = 10; // Center offset from top-left (35/2 ≈ 18), could have done ROUND(35/2) = 18

  const starElement = document.getElementById('map-star');
  const mapImg = document.querySelector('.map-img');
  if (!starElement || !mapImg) return;
  
  // Get map location from current day's data
  const selectedDay = getCurrentDay();
  const mapLocation = ADVENT_DATA[selectedDay]?.map_location;
  
  // if bad coordinates, hide map
  if (!mapLocation || (mapLocation.trim() == '')) {
    starElement.style.display = 'none';
    return;
  }

  // Parse coordinates from "299, 304" format, if bad coordinates, hide map
  const coords = mapLocation.split(',').map(coord => parseInt(coord.trim()));
  if (coords.length !== 2 || isNaN(coords[0]) || isNaN(coords[1])) {
    starElement.style.display = 'none';
    return;
  }
      
  // Get the actual rendered size of the map image
  const mapRect = mapImg.getBoundingClientRect();//e.g. 552x647px
  const mapContainerRect = mapImg.parentElement.getBoundingClientRect();
  
  // Calculate the scale factor based on the actual rendered size vs natural size
  const scaleX = mapRect.width / 552; //mapImg.naturalWidth;
  const scaleY = mapRect.height / 647; //mapImg.naturalHeight;
  
  // Apply the scale to the center coordinates, not the top left corner
  const starCenterX = (coords[0] + BASE_STAR_CENTER_OFFSET);
  const starCenterY = (coords[1] + BASE_STAR_CENTER_OFFSET);

  //bound size of star to be visible - will be slightly off center but that is fine for edge cases
  //const starSizeW = Math.max(BASE_STAR_SIZE * scaleX, 20);
  //const starSizeH = Math.max(BASE_STAR_SIZE * scaleY, 20);

  // Calculate the top left corner of the star, taking into account the rescaling
  const scaledStarLeftX = (starCenterX * scaleX) - ((BASE_STAR_SIZE / 2.0) * scaleX);
  const scaledStarTopY = (starCenterY * scaleY) - ((BASE_STAR_SIZE / 2.0) * scaleY);
    
  // NEW APPROACH: Calculate absolute offset from viewport to image
  // This accounts for flexbox centering and any other positioning
  let imageOffsetX = mapRect.left - mapContainerRect.left;
  let imageOffsetY = mapRect.top - mapContainerRect.top;
  
  /* FIXME: this is a temporary fix for the star position on mobile. need to fix landscape later */
  if (isMobile()) {
    imageOffsetX -= 5;
    imageOffsetY -= 3;
  }

  // TODO: add some padding to the star so it's not right on the edge of the map
  // Position star so its center aligns with the target coordinates
  // Final position = scaled center - scaled offset
  starElement.style.left = (scaledStarLeftX + imageOffsetX) + 'px';
  starElement.style.top = (scaledStarTopY + imageOffsetY) + 'px';
  starElement.style.width = BASE_STAR_SIZE + 'px';
  starElement.style.height = BASE_STAR_SIZE + 'px';
  starElement.style.display = 'block';
  
  // Apply scale to star (this will work with the animation)
  // randomly taking scaleX, scaleY would be fine here too or could do Math.min(scaleX, scaleY)
  // easier just to choose one and won't make a visual difference
  starElement.style.transform = `scale(${scaleX})`;
  
  /*console.log('Star positioning:', {
    originalCoords: coords,
    mapNaturalSize: { width: mapImg.naturalWidth, height: mapImg.naturalHeight },
    mapRenderedSize: { width: mapRect.width, height: mapRect.height },
    containerSize: { width: mapContainerRect.width, height: mapContainerRect.height },
    imageOffset: { x: imageOffsetX, y: imageOffsetY },
    scale: { x: scaleX, y: scaleY },
    baseStarSize: BASE_STAR_SIZE,
    baseStarCenterOffset: BASE_STAR_CENTER_OFFSET,
    scaledCoords: [scaledStarLeftX, scaledStarTopY],
    finalPosition: [scaledStarLeftX + imageOffsetX, scaledStarTopY + imageOffsetY]
  });*/
} 


// take www.cnn.com and convert to https://www.cnn.com that can be used in anchor tags
function normalizeExternalUrl(input) {
  if (!input) return '';

  // Trim spaces
  let url = input.trim();

  // If missing a scheme, prepend http://
  if (!/^https?:\/\//i.test(url)) {
    url = 'http://' + url;
  }

  try {
    const u = new URL(url);
    // Always return the normalized origin + pathname + trailing slash
    let normalized = u.origin + (u.pathname.endsWith('/') ? u.pathname : u.pathname + '/');
    return normalized;
  } catch (e) {
    // If URL constructor fails, return empty or fallback
    console.warn('Invalid URL:', input);
    return '';
  }
}


function startSnowAnimation() {
  const snowContainer = document.getElementById('snow-container');

  if (snowContainer) {
    snowContainer.style.display = 'block'; // Make the snow scene visible
    
    // Check if snow animation is enabled
    const enableSnow = snowContainer.getAttribute('data-enable-snow');
    if (enableSnow === 'true') {
      setInterval(createSnowflake, 150);
    }
  }

  return;
}

function showDailyRevealSections() {
  // Array of section IDs to show, these are set to be hidden by default and then turned on

  const sectionIds = [
    'daily-video-section',
    'day-overview-section',
    'map-section',
    'explore-and-feedback',
    'celestial-section', 
    'daily_feedback_section',
    'preorder-section',
    'explore-more-section'
  ];

  // Loop through each section ID and show it if found
  sectionIds.forEach(sectionId => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.style.display = 'block';
    }
  });
}

function hideDailyRevealSections() {
  // Array of section IDs to hide
  const sectionIds = [
    'daily-video-section',
    'day-overview-section',
    'explore-and-feedback',
    'map-section',
    'celestial-section', 
    'daily_feedback_section',
    'preorder-section',
    'explore-more-section'
  ];

  // Loop through each section ID and hide it if found
  sectionIds.forEach(sectionId => {
    const section = document.getElementById(sectionId);
    if (section) {
      section.style.display = 'none';
    }
  });
}

// Show sections that should be visible on day 0 (start video)
function showDayZeroSections() {
  const startVideoSection = document.getElementById('start-video');
  if (startVideoSection) {
    startVideoSection.style.display = 'block';
    
    // Manually trigger video playback when showing day 0 section
    // 
    // PROBLEM: When switching back to day 0, the start video section is shown (display: block),
    // but the video remains paused even though IntersectionObserver tries to play it.
    // 
    // ROOT CAUSE: Browser autoplay restrictions and timing issues:
    // 1. IntersectionObserver detects visibility and calls play() immediately (~0-100ms)
    // 2. Browser's autoplay policy may pause the video shortly after (~100-400ms)
    // 3. If we call play() before the browser pauses it, our call may be ignored
    // 4. If we call play() after the browser pauses it, our call succeeds
    //
    // WORKAROUND: Delay manual play() call by BROWSER_VIDEO_LOADING_WORKAROUND milliseconds
    // This ensures the browser has completed its autoplay pause behavior before we attempt
    // to manually start playback. The delay is long enough to let the browser pause, but
    // short enough that users don't notice a delay.
    //
    // This happens when:
    // - User loads day 0 (autoplays initially)
    // - User switches to day 3 (video is hidden and paused)
    // - User switches back to day 0 (section shown, but video remains paused without this workaround)
    //
    // Without this workaround, the video will appear but not play.
    setTimeout(() => {
      const player = g_videoPlayers['start-video-iframe'];
      if (player) {
        // Check if video is already playing (IntersectionObserver may have started it)
        player.getPaused().then(paused => {
          if (paused) {
            // Video is paused, so manually start it
            // This is the expected case - browser has paused it, now we can successfully play it
            return player.ready().then(() => {
              // Reset to beginning and play
              return player.setCurrentTime(0).then(() => {
                return player.play();
              });
            }).then(() => {
              console.log('Start video playing (day 0 selected - manual play after browser pause)');
            }).catch(error => {
              // Expected if browser blocks autoplay - IntersectionObserver will handle it as fallback
              console.log('Could not play start video manually (autoplay may be blocked):', error.message);
            });
          } else {
            // Video is already playing, just reset to beginning
            // This is the unexpected case - IntersectionObserver successfully played it
            return player.setCurrentTime(0).then(() => {
              console.log('Start video already playing, reset to beginning');
            });
          }
        }).catch(error => {
          console.log('Could not check video paused state:', error.message);
        });
      }
    }, BROWSER_VIDEO_LOADING_WORKAROUND); // Delay to allow browser autoplay pause to complete
  }
}

// Hide sections that should only be visible on day 0 (start video)
function hideDayZeroSections() {
  const startVideoSection = document.getElementById('start-video');
  if (startVideoSection) {
    startVideoSection.style.display = 'none';
  }
}

// Initialize Vimeo players for Coming Soon and Start Video sections
// 
// This function may be called from two places:
// 1. DOMContentLoaded event (line 120) - if script loads before DOMContentLoaded
// 2. Script onload callback (in liquid file) - if script loads after DOMContentLoaded
//
// The function includes a guard to prevent double initialization and uses waitForVimeoAPI()
// to ensure the Vimeo API is ready before proceeding.
//
// Execution flow:
// - Wait for Vimeo API to be ready (using centralized promise system)
// - If videos already initialized, skip (prevents double initialization)
// - Otherwise, initialize Vimeo.Player instances for each iframe
// - Store player instances in g_videoPlayers for use by IntersectionObserver
// 
// Note: This function does NOT call .play() - playback is handled by
// setupVideoIntersectionObserver() based on section visibility
function initializeVideos() {
  // Guard: Prevent double initialization
  if (g_videosInitialized) {
    console.log('Videos already initialized, skipping');
    return;
  }

  // Hardcoded array of iframe IDs to initialize
  // Note: Daily Reveal video (daily-video-iframe) is initialized separately in setDailyRevealVideoSimple() / setDailyRevealVideoAdvanced()
  // Note: Coming Soon video uses HTML5 <video> element, not Vimeo iframe, so it's not initialized here
  const iframeIds = [
    'start-video-iframe'
  ];

  // Wait for Vimeo API to be ready before proceeding
  // This prevents race conditions where code tries to use Vimeo.Player before the script loads
  waitForVimeoAPI().then(() => {
    initializeVideosInternal(iframeIds);
  }).catch(error => {
    console.error('Failed to initialize videos - Vimeo API not available:', error.message);
    // Don't retry here - waitForVimeoAPI() already has timeout handling
  });
}

// Internal function that actually initializes the videos
// Separated from initializeVideos() to allow waitForVimeoAPI() to be called first
function initializeVideosInternal(iframeIds) {

  // Loop through each iframe ID and initialize the Vimeo player
  // Expected execution order:
  // 1. Script loads and onload fires, OR DOMContentLoaded fires
  // 2. waitForVimeoAPI() ensures window.Vimeo is available
  // 3. This function runs (window.Vimeo is guaranteed to be available)
  // 4. Each iframe gets a Vimeo.Player instance created
  // 5. Flag is set to prevent double initialization
  let initializedCount = 0;
  iframeIds.forEach(iframeId => {
    // Get the iframe element by ID
    const iframe = document.getElementById(iframeId);
    if (!iframe) {
      console.log(`Video iframe not found: ${iframeId}`);
      return;
    }

    // Initialize Vimeo player for this iframe
    // This creates a Vimeo.Player instance that allows programmatic control
    // (play, pause, events, etc.) without interfering with the iframe's src attribute
    // Player instances are stored in g_videoPlayers for use by IntersectionObserver
    try {
      const player = new Vimeo.Player(iframe);
      console.log(`Vimeo player initialized for: ${iframeId}`);
      
      // Store player instance for use by IntersectionObserver
      // IntersectionObserver will handle play/pause based on visibility
      g_videoPlayers[iframeId] = player;
      
      initializedCount++;
    } catch (error) {
      console.log(`Error initializing Vimeo player for ${iframeId}:`, error);
    }
  });

  // Mark as initialized if at least one video was initialized
  if (initializedCount > 0) {
    g_videosInitialized = true;
    console.log(`Videos initialization complete: ${initializedCount} of ${iframeIds.length} initialized`);
  }
}

// Setup IntersectionObserver for video sections to play/pause based on visibility
// 
// This function watches video sections and automatically plays/pauses videos when they
// become visible or scroll out of view. This prevents multiple videos playing simultaneously
// and respects browser autoplay policies by only playing the currently visible video.
//
// Configuration:
// - Threshold: 0.5 (50% of element must be visible to trigger)
// - Root margin: "0px 0px -20% 0px" (excludes bottom 20% of viewport)
//   This means the intersection root is effectively the top 80% of the viewport.
//   Videos will trigger when 50% of the section is visible within that top 80% area.
// - Pause on scroll away: Enabled (videos pause when intersection ratio < threshold)
//
// Execution flow:
// 1. Maps section IDs to iframe IDs
// 2. Creates IntersectionObserver with threshold and rootMargin
// 3. Observes each video section
// 4. On intersection change:
//    - If ratio >= 0.5 and autoplay=1: play video
//    - If ratio < 0.5: pause video
// 5. Handles initial visibility (first section on page load)
function setupVideoIntersectionObserver() {
  // Map section IDs to iframe IDs
  // This allows us to find the corresponding player instance for each section
  // Note: Coming Soon video uses HTML5 <video> element and always plays, so it's not observed here
  const sectionToIframeMap = {
    'start-video': 'start-video-iframe',
    'daily-video-section': 'daily-video-iframe'
  };

  // Check if IntersectionObserver is supported (fallback for older browsers)
  if (typeof IntersectionObserver === 'undefined') {
    console.log('IntersectionObserver not supported, videos will rely on URL autoplay parameters');
    return;
  }

  // Create IntersectionObserver with configured threshold and rootMargin
  // 
  // Threshold: 0.5 means 50% of the element must be visible within the intersection root
  // Root margin: "0px 0px -20% 0px" means:
  //   - Top: 0px (no offset)
  //   - Right: 0px (no offset)
  //   - Bottom: -20% (excludes bottom 20% of viewport from intersection root)
  //   - Left: 0px (no offset)
  // 
  // This effectively creates an intersection root that is the top 80% of the viewport.
  // Videos will trigger when 50% of the section is visible within that top 80% area,
  // which helps ensure the video is meaningfully visible before playing.
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      const sectionId = entry.target.id;
      const iframeId = sectionToIframeMap[sectionId];
      
      if (!iframeId) {
        console.log(`No iframe mapping found for section: ${sectionId}`);
        return;
      }

      const player = g_videoPlayers[iframeId];
      if (!player) {
        console.log(`No player instance found for iframe: ${iframeId}`);
        return;
      }

      // Get the iframe element to check for autoplay parameter
      const iframe = document.getElementById(iframeId);
      if (!iframe) {
        return;
      }

      const iframeSrc = iframe.src || '';
      const hasAutoplay = iframeSrc.includes('autoplay=1');

      // Intersection ratio >= 0.5 means section is sufficiently visible
      if (entry.intersectionRatio >= 0.5) {
        // Only play if autoplay=1 is specified in URL
        if (hasAutoplay) {
          player.ready().then(() => {
            return player.play();
          }).then(() => {
            console.log(`Video playing for: ${iframeId} (section visible)`);
          }).catch(error => {
            // Expected if browser blocks autoplay - URL parameters should still work
            console.log(`Could not play video for ${iframeId} (autoplay may be blocked):`, error.message);
          });
        }
        
        // Re-add overlay when video section scrolls back into view (Safari desktop)
        if (sectionId === 'daily-video-section') {
          addDailyVideoOverlay();
          setupDailyVideoOverlayClickHandler();
        }
      } else {
        // Intersection ratio < 0.5 means section is not sufficiently visible
        // Pause the video when it scrolls away (desktop only - mobile continues playing)
        if (!isMobile()) {
          player.getPaused().then(paused => {
            if (!paused) {
              return player.pause();
            }
          }).then(() => {
            console.log(`Video paused for: ${iframeId} (section not visible)`);
          }).catch(error => {
            // Ignore errors when pausing (video may already be paused)
            console.log(`Could not pause video for ${iframeId}:`, error.message);
          });
          
          // Re-add overlay when video section scrolls away (Safari desktop)
          if (sectionId === 'daily-video-section') {
            addDailyVideoOverlay();
            setupDailyVideoOverlayClickHandler();
          }
        }
      }
    });
  }, {
    threshold: 0.5, // 50% of element must be visible
    rootMargin: '0px 0px -20% 0px' // Excludes bottom 20% of viewport
  });

  // Observe each video section
  // IntersectionObserver will fire immediately if a section is already intersecting
  // This handles the case where the first section is visible on page load
  Object.keys(sectionToIframeMap).forEach(sectionId => {
    const section = document.getElementById(sectionId);
    if (section) {
      observer.observe(section);
      console.log(`IntersectionObserver set up for section: ${sectionId}`);
    } else {
      console.log(`Section not found: ${sectionId}`);
    }
  });
}

// Setup IntersectionObserver for celestial section video to play/pause based on visibility
function setupCelestialVideoObserver() {
  const celestialSection = document.getElementById('celestial-section');
  const celestialVideo = document.getElementById('celestial-video');
  
  if (!celestialSection || !celestialVideo) {
    console.log('Celestial section or video not found');
    return;
  }

  // Check if IntersectionObserver is supported
  if (typeof IntersectionObserver === 'undefined') {
    console.log('IntersectionObserver not supported for celestial video');
    return;
  }

  // Create IntersectionObserver with 50% threshold (same as other videos)
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.intersectionRatio >= 0.5) {
        // Section is sufficiently visible - play video
        celestialVideo.play().then(() => {
          console.log('Celestial video playing (section visible)');
        }).catch(error => {
          // Expected if browser blocks autoplay
          console.log('Could not play celestial video (autoplay may be blocked):', error.message);
        });
      } else {
        // Section is not sufficiently visible - pause video
        if (!celestialVideo.paused) {
          celestialVideo.pause();
          console.log('Celestial video paused (section not visible)');
        }
      }
    });
  }, {
    threshold: 0.5, // 50% of element must be visible
    rootMargin: '0px 0px -20% 0px' // Excludes bottom 20% of viewport (same as other videos)
  });

  // Start observing the celestial section
  observer.observe(celestialSection);
  console.log('IntersectionObserver set up for celestial video');
}

// small animation on map star showing collaborator's location on the map
function initializeStarAnimation() {
  const mapSection = document.getElementById('map-section');
  const starElement = document.getElementById('map-star');
  
  if (!mapSection || !starElement) return;

  // Create intersection observer
  const observer = new IntersectionObserver((entries) => {
    entries.forEach(entry => {
      if (entry.isIntersecting) {
        // Section is in view - animate star in
        starElement.classList.add('animate-in');
      } else {
        // Section is out of view - animate star out
        starElement.classList.remove('animate-in');
      }
    });
  }, {
    threshold: 0.75, // Trigger when 30% of the section is visible
    rootMargin: '0px 0px -10% 0px' // Trigger slightly before the section is fully in view
  });

  // Start observing the map section
  observer.observe(mapSection);
}

function createSnowflake() {
  const snowflake = document.createElement("div");
  snowflake.classList.add("snowflake");

  // Random horizontal position
  snowflake.style.left = Math.random() * 100 + "vw";

  // Random size
  const size = Math.random() * 3 + 3 + "px";
  snowflake.style.width = size;
  snowflake.style.height = size;

  // Random animation duration (slower fall)
  snowflake.style.animationDuration = Math.random() * 8 + 16 + "s";

  const snowContainer = document.getElementById('snow-container');
  if (snowContainer) {
    snowContainer.appendChild(snowflake);

    // Remove the snowflake after it falls
    setTimeout(() => {
      if (snowflake.parentNode) {
        snowflake.remove();
      }
    }, 15000);/* if this is 5 seconds, the snowflakes will disappear after about 350px */
  }
}

// Coming Soon Overlay Image Width Control
function updateOverlayImageWidth() {
  const overlayImages = document.querySelectorAll('.coming-soon-overlay-responsive');
  if (!overlayImages.length) return;
  
  overlayImages.forEach(overlayImage => {
    const desktopWidth = overlayImage.getAttribute('data-width-desktop');
    const mobileWidth = overlayImage.getAttribute('data-width-mobile');
    
    if (window.innerWidth <= 768) {
      // Mobile
      overlayImage.style.setProperty('width', mobileWidth + '%', 'important');
    } else {
      // Desktop
      overlayImage.style.setProperty('width', desktopWidth + '%', 'important');
    }
  });
}



// Function to update daily video based on selected day
function updateDailyVideo() {
  // TEMPORARY: Disabled for debugging form state (comment out this line to re-enable)
  //return;
  
  const selectedDay = getCurrentDay();

  // Don't update video when day is 0 (intro/start state)
  if (selectedDay == 0) {
    const videoSection = document.getElementById('daily-video-section');
    if (videoSection) {
      videoSection.style.display = 'none';
    }
    return;
  }
  
  const videoSection = document.getElementById('daily-video-section');
  const videoIframe = document.getElementById('daily-video-iframe');
  
  if (!videoSection || !videoIframe || !ADVENT_DATA) return;
  
  const dailyData = ADVENT_DATA[selectedDay];
  
  
  // Hide video section if no video data for this day
  if (!dailyData || !dailyData.vimeo_id) {
    videoSection.style.display = 'none';
    return;
  }
  
  // Show video section and update content
  videoSection.style.display = 'block';
  
  // Re-add overlay shield when switching days (Safari desktop only)
  addDailyVideoOverlay();
  setupDailyVideoOverlayClickHandler();
  
  // Before switching videos, save the current mute state
  const oldPlayer = g_videoPlayers['daily-video-iframe'];
  if (oldPlayer) {
    oldPlayer.getMuted().then(muted => {
      g_dailyVideoMuted = muted;
    }).catch(error => {
      // If we can't get mute state, keep current preference
      console.log('Could not get mute state from old video:', error.message);
    });
  }
  
  if (!dailyData.vimeo_id || !dailyData.vimeo_hash) {
    // handle more graceful fallback later -- like showing a placeholder image
    // but at the least, clear the last video player instance
    if (oldPlayer) {
      oldPlayer.getPaused().then(paused => {
        if (!paused) {
          return oldPlayer.pause();
        }
      });
    }
    return;
  }
  
  if (USE_ADVANCED_VIDEO_PLAYER) {
    setDailyRevealVideoAdvanced(dailyData.vimeo_id, dailyData.vimeo_hash);
  } else {
    setDailyRevealVideoSimple(dailyData.vimeo_id, dailyData.vimeo_hash);
  }
  
    // Update title based on day
  let prettyDate = writePrettyDay(selectedDay);
  
  // Update title based on day
  if (selectedDay >= 1) {
    const title = `${prettyDate} - Dandelion Chocolate`;
    videoIframe.setAttribute('title', title);
    videoIframe.setAttribute('aria-label', title);
  } else {
    videoIframe.setAttribute('title', 'Dandelion Chocolate Advent Calendar');
    videoIframe.setAttribute('aria-label', 'Dandelion Chocolate Advent Calendar');
  }
}

// Helper function to check if a section is currently visible in viewport
// Uses getBoundingClientRect() to determine if section is in viewport
// Returns true if section is at least 50% visible (matching IntersectionObserver threshold)
function isSectionVisible(sectionElement) {
  if (!sectionElement) return false;
  
  const rect = sectionElement.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  
  // Calculate visible height (intersection with viewport)
  // Account for rootMargin: "0px 0px -20% 0px" (top 80% of viewport)
  const viewportTop = 0;
  const viewportBottom = viewportHeight * 0.8; // Top 80% of viewport
  
  const sectionTop = Math.max(rect.top, viewportTop);
  const sectionBottom = Math.min(rect.bottom, viewportBottom);
  const visibleHeight = Math.max(0, sectionBottom - sectionTop);
  
  // Check if at least 50% of section is visible (matching threshold)
  const sectionHeight = rect.height;
  const visibleRatio = sectionHeight > 0 ? visibleHeight / sectionHeight : 0;
  
  return visibleRatio >= 0.5;
}

// Function to properly set Vimeo video source for dynamic swapping
// Simple version: integrated with IntersectionObserver to prevent autoplay conflicts
function setDailyRevealVideoSimple(vimeoId, vimeoHash) {
  const el = document.getElementById('daily-video-iframe');
  if (!el) return;
  
  // Wait for Vimeo API to be ready before proceeding
  // This prevents race conditions where code tries to use Vimeo.Player before the script loads
  waitForVimeoAPI().then(() => {
    setDailyRevealVideoSimpleInternal(vimeoId, vimeoHash, el);
  }).catch(error => {
    console.error('Failed to set daily reveal video - Vimeo API not available:', error.message);
    // Still set the iframe src so the video can load via URL parameters
    // User will be able to play it manually even if API isn't available
    // Add null checks to prevent errors if vimeoId or vimeoHash are missing
    if (!vimeoId || !vimeoHash) {
      console.warn('Cannot set video iframe src - missing vimeoId or vimeoHash');
      return;
    }
    const base = 'https://player.vimeo.com/video/';
    const params = `?h=${encodeURIComponent(vimeoHash)}&autoplay=1&muted=1&loop=1&playsinline=1&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479&texttrack=en-x-autogen`;
    el.src = base + encodeURIComponent(vimeoId) + params;
  });
}

// Internal function that actually sets the video
// Separated from setDailyRevealVideoSimple() to allow waitForVimeoAPI() to be called first
function setDailyRevealVideoSimpleInternal(vimeoId, vimeoHash, el) {
  // Pause old video if player instance exists (handles day change scenario)
  // Also save mute state before switching
  const oldPlayer = g_videoPlayers['daily-video-iframe'];
  if (oldPlayer) {
    // Save mute state before pausing
    oldPlayer.getMuted().then(muted => {
      g_dailyVideoMuted = muted;
    }).catch(error => {
      // If we can't get mute state, keep current preference
      console.log('Could not get mute state from old video:', error.message);
    });
    
    oldPlayer.getPaused().then(paused => {
      if (!paused) {
        return oldPlayer.pause();
      }
    }).catch(error => {
      // Ignore errors when pausing old video
      console.log('Could not pause old video:', error.message);
    });
  }
  
  const base = 'https://player.vimeo.com/video/';
  const params = `?h=${encodeURIComponent(vimeoHash)}&autoplay=1&muted=1&loop=1&playsinline=1&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479&texttrack=en-x-autogen`;
  el.src = base + encodeURIComponent(vimeoId) + params;
  
  // Initialize Vimeo player and hide placeholder when video plays
  // Use setTimeout to allow iframe to load before creating player instance
  setTimeout(() => {
    const player = new Vimeo.Player(el);
    
    // Store player instance in g_videoPlayers for IntersectionObserver
    // This allows the observer to control playback based on visibility
    g_videoPlayers['daily-video-iframe'] = player;
    
    player.on('play', () => {
      const placeholder = document.querySelector('.daily-unveil-video-placeholder');
      if (placeholder) {
        placeholder.style.display = 'none';
      }
    });
    
    // Listen for mute/unmute changes to update global preference
    player.on('volumechange', () => {
      player.getMuted().then(muted => {
        g_dailyVideoMuted = muted;
      }).catch(error => {
        console.log('Could not get mute state after volume change:', error.message);
      });
    });
    
    // Restore user's mute preference after player is ready
    player.ready().then(() => {
      return player.setMuted(g_dailyVideoMuted);
    }).then(() => {
      console.log(`Daily reveal video mute state restored: ${g_dailyVideoMuted ? 'muted' : 'unmuted'}`);
      
      // Check if mobile and autoplay failed (video is paused)
      if (isMobile()) {
        return player.getPaused().then(paused => {
          if (paused) {
            addPlayButtonOverlay();
          }
        }).catch(error => {
          // If we can't check pause state, don't show overlay (fail-safe)
          console.log('Could not check pause state for overlay:', error.message);
        });
      }
    }).catch(error => {
      // Ignore errors - video may not support mute control or may be blocked
      console.log('Could not set mute state on new video:', error.message);
    });
    
    // Check if section is currently visible
    // If not visible, pause immediately to prevent unwanted autoplay
    // IntersectionObserver will handle playing when section becomes visible
    const videoSection = document.getElementById('daily-video-section');
    if (videoSection && !isSectionVisible(videoSection)) {
      player.ready().then(() => {
        return player.pause();
      }).then(() => {
        console.log('Daily reveal video paused (section not visible)');
      }).catch(error => {
        // Ignore errors - video may not be ready yet or already paused
        console.log('Could not pause video immediately:', error.message);
      });
    } else if (videoSection && isSectionVisible(videoSection)) {
      // Section is visible - IntersectionObserver will handle playing
      // But we can also try to play here as a fallback
      // (Observer may have already fired or will fire soon)
      console.log('Daily reveal video section is visible, IntersectionObserver will handle playback');
    }
  }, BROWSER_VIDEO_LOADING_WORKAROUND);
}


// Function to properly set Vimeo video source for dynamic swapping
// Debugging version that reuses existing player instance instead of creating new one
// Useful for testing/debugging to avoid player recreation overhead
function logDailyVideoDebug(message, extra) {
  if (extra !== undefined) {
    console.log('[DailyVideoDebug]', message, extra);
  } else {
    console.log('[DailyVideoDebug]', message);
  }
}

function setDailyRevealVideoAdvanced(vimeoId, vimeoHash) {
  const el = document.getElementById('daily-video-iframe');
  if (!el) return;

  // Wait for Vimeo API to be ready before proceeding
  // This prevents race conditions where code tries to use Vimeo.Player before the script loads
  waitForVimeoAPI().then(() => {
    setDailyRevealVideoAdvancedInternal(vimeoId, vimeoHash, el);
  }).catch(error => {
    console.error('Failed to set daily reveal video advanced - Vimeo API not available:', error.message);
    // Still set the iframe src so the video can load via URL parameters
    // User will be able to play it manually even if API isn't available
    // Add null checks to prevent errors if vimeoId or vimeoHash are missing
    if (!vimeoId || !vimeoHash) {
      console.warn('Cannot set video iframe src - missing vimeoId or vimeoHash');
      return;
    }
    const base = 'https://player.vimeo.com/video/';
    const params = `?h=${encodeURIComponent(vimeoHash)}&autoplay=1&muted=1&loop=1&playsinline=1&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479&texttrack=en-x-autogen`;
    el.src = base + encodeURIComponent(vimeoId) + params;
  });
}

// Internal function that actually sets the video for advanced mode
// Separated from setDailyRevealVideoAdvanced() to allow waitForVimeoAPI() to be called first
// This function assumes window.Vimeo is available (guaranteed by waitForVimeoAPI())
function setDailyRevealVideoAdvancedInternal(vimeoId, vimeoHash, el) {
  const videoId = (vimeoId != null && vimeoId != undefined) ? vimeoId.toString() : '';
  const videoHash = (vimeoHash != null && vimeoHash != undefined) ? vimeoHash.toString() : '';

  if (!videoId || !videoHash) {
    console.warn('Missing Vimeo ID or hash for debugging video load');
    return;
  }

  const getIframeReadyState = () => {
    // Check if iframe element exists first
    if (!el) {
      return 'iframe-not-found';
    }
    
    // Wrap contentWindow and document access in try-catch to handle cross-origin errors
    // Accessing contentWindow.document on cross-origin iframes throws SecurityError
    // This is expected for Vimeo iframes and should not break the console
    try {
      const contentWindow = el.contentWindow;
      if (!contentWindow) {
        return 'unknown';
      }
      // Accessing document on cross-origin iframe throws SecurityError
      // Wrap in separate try-catch to handle this specific error
      try {
        const doc = contentWindow.document;
        return doc ? (doc.readyState || 'unknown') : 'unknown';
      } catch (docError) {
        // SecurityError when accessing document on cross-origin iframe
        return 'unavailable(cross-origin)';
      }
    } catch (err) {
      // Any other error accessing contentWindow
      return 'unavailable(cross-origin)';
    }
  };

  const pauseOldPlayer = () => {
    const existingPlayer = g_videoPlayers['daily-video-iframe'];
    if (existingPlayer) {
      // Save mute state before pausing
      existingPlayer.getMuted().then(muted => {
        g_dailyVideoMuted = muted;
      }).catch(error => {
        // If we can't get mute state, keep current preference
        logDailyVideoDebug('Could not get mute state from old video:', error.message);
      });
      
      existingPlayer.getPaused().then(paused => {
        if (!paused) {
          return existingPlayer.pause();
        }
      }).catch(error => {
        console.log('Could not pause old video:', error.message);
      });
    }
  };

  const scheduleDelayedAutoplay = (playerInstance, context) => {
    setTimeout(() => {
      playerInstance.getPaused().then(paused => {
        if (!paused) return;
        return playerInstance.ready().then(() => playerInstance.play()).then(() => {
          console.log(`Daily reveal video autoplay resumed after delay (${context})`);
        });
      }).catch(error => {
        console.log('Delayed autoplay check failed:', error?.message || error);
      });
    }, BROWSER_VIDEO_LOADING_WORKAROUND);
  };

  const applyVisibilityGuards = (playerInstance) => {
    const videoSection = document.getElementById('daily-video-section');
    if (videoSection && !isSectionVisible(videoSection)) {
      playerInstance.ready().then(() => playerInstance.pause()).then(() => {
        console.log('Daily reveal video paused (section not visible)');
      }).catch(error => {
        console.log('Could not pause video immediately:', error.message);
      });
    } else if (videoSection && isSectionVisible(videoSection)) {
      console.log('Daily reveal video section is visible, IntersectionObserver will handle playback');
    }
  };

  const handlePostLoadState = (playerInstance, context) => {
    applyVisibilityGuards(playerInstance);
    scheduleDelayedAutoplay(playerInstance, context);
  };

  const fallbackLoadVideo = () => {
    const existingPlayer = g_videoPlayers['daily-video-iframe'];
    if (!existingPlayer) {
      console.warn('Fallback loadVideo requested but no existing player is available');
      return;
    }

    logDailyVideoDebug('fallback loadVideo() invoked', { videoId, videoHash });
    existingPlayer.loadVideo(videoId, {
      hash: videoHash,
      autoplay: true,
      muted: true,
      loop: true,
      playsinline: true,
      title: false,
      byline: false,
      portrait: false,
      badge: false,
      autopause: false,
      player_id: 0,
      app_id: 58479,
      texttrack: 'en-x-autogen'
    }).then(() => {
      logDailyVideoDebug('fallback loadVideo() resolved - resetting time to 0');
      return existingPlayer.setCurrentTime(0).catch(error => {
        logDailyVideoDebug('Could not reset video to start after loadVideo()', error?.message || error);
      });
    }).then(() => {
      handlePostLoadState(existingPlayer, 'fallback');
    }).catch(error => {
      logDailyVideoDebug('Error loading video in debugging mode (fallback)', error);
    });
  };

  pauseOldPlayer();
  logDailyVideoDebug('setDailyRevealVideoAdvanced primary flow start', { videoId, videoHash, iframeReadyState: getIframeReadyState() });

  // Primary path: mirror production flow by resetting iframe src and creating a fresh player
  const base = 'https://player.vimeo.com/video/';
  const params = `?h=${encodeURIComponent(videoHash)}&autoplay=1&muted=1&loop=1&playsinline=1&title=0&byline=0&portrait=0&badge=0&autopause=0&player_id=0&app_id=58479&texttrack=en-x-autogen`;

  let loadHandled = false;
  let timeoutId = null;

  const cleanupListeners = (loadHandler) => {
    if (loadHandler) {
      el.removeEventListener('load', loadHandler);
    }
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
  };

  const initializeNewPlayer = () => {
    try {
      const player = new Vimeo.Player(el);
      g_videoPlayers['daily-video-iframe'] = player;

      player.on('play', () => {
        const placeholder = document.querySelector('.daily-unveil-video-placeholder');
        if (placeholder) {
          placeholder.style.display = 'none';
        }
      });

      // Listen for mute/unmute changes to update global preference
      player.on('volumechange', () => {
        player.getMuted().then(muted => {
          g_dailyVideoMuted = muted;
        }).catch(error => {
          console.log('Could not get mute state after volume change:', error.message);
        });
      });

      // Restore user's mute preference after player is ready
      player.ready().then(() => {
        return player.setMuted(g_dailyVideoMuted);
      }).then(() => {
        logDailyVideoDebug(`Daily reveal video mute state restored: ${g_dailyVideoMuted ? 'muted' : 'unmuted'}`);
        
        // Check if mobile and autoplay failed (video is paused)
        if (isMobile()) {
          return player.getPaused().then(paused => {
            if (paused) {
              addPlayButtonOverlay();
            }
          }).catch(error => {
            // If we can't check pause state, don't show overlay (fail-safe)
            logDailyVideoDebug('Could not check pause state for overlay:', error.message);
          });
        }
      }).catch(error => {
        // Ignore errors - video may not support mute control or may be blocked
        logDailyVideoDebug('Could not set mute state on new video:', error.message);
      });

      handlePostLoadState(player, 'primary');
    } catch (error) {
      logDailyVideoDebug('Error creating Vimeo player in debugging mode', error);
      fallbackLoadVideo();
    }
  };

  const loadHandler = () => {
    logDailyVideoDebug('iframe load event fired', { iframeReadyState: getIframeReadyState() });
    loadHandled = true;
    cleanupListeners(loadHandler);
    // Allow iframe a moment to finish booting Vimeo before instantiating
    setTimeout(() => {
      if (!el.src || !el.src.includes('player.vimeo.com')) {
        logDailyVideoDebug('Iframe src missing Vimeo domain after load; falling back');
        fallbackLoadVideo();
        return;
      }
      initializeNewPlayer();
    }, 100);
  };

  el.addEventListener('load', loadHandler);

  timeoutId = setTimeout(() => {
    if (!loadHandled) {
      logDailyVideoDebug('Daily reveal iframe load timeout - attempting fallback', { iframeReadyState: getIframeReadyState() });
      cleanupListeners(loadHandler);
      fallbackLoadVideo();
    } else {
      logDailyVideoDebug('Load timeout fired after load handled (no action)', { iframeReadyState: getIframeReadyState() });
    }
  }, BROWSER_VIDEO_LOADING_WORKAROUND * 2);

  // Setting src triggers the load event
  const newSrc = base + encodeURIComponent(videoId) + params;
  logDailyVideoDebug('Setting iframe src for primary flow', { newSrc });
  el.src = newSrc;
}


// Function to add play button overlay for mobile autoplay fallback
function addPlayButtonOverlay() {
  // Only show overlay on mobile devices
  if (!isMobile()) {
    return;
  }
  
  const videoContainer = document.querySelector('.daily-video-wrapper');
  if (!videoContainer) return;
  
  // Remove existing overlay if it exists
  const existingOverlay = videoContainer.querySelector('.video-play-overlay');
  if (existingOverlay) {
    existingOverlay.remove();
  }
  
  // Create play button overlay
  const overlay = document.createElement('div');
  overlay.className = 'video-play-overlay';
  overlay.innerHTML = `
    <button class="play-button" aria-label="Play video">
      <svg width="60" height="60" viewBox="0 0 60 60" fill="none">
        <circle cx="30" cy="30" r="30" fill="rgba(0,0,0,0.7)"/>
        <path d="M22 18L42 30L22 42V18Z" fill="white"/>
      </svg>
    </button>
  `;
  
  // Add click handler to play video
  const playButton = overlay.querySelector('.play-button');
  playButton.addEventListener('click', function() {
    const iframe = document.getElementById('daily-video-iframe');
    if (iframe) {
      // Try to play the video using Vimeo API
      if (window.Vimeo && iframe.contentWindow) {
        const player = new Vimeo.Player(iframe);
        player.play().then(() => {
          overlay.style.display = 'none';
        }).catch(() => {
          // If Vimeo API fails, try direct iframe interaction
          iframe.contentWindow.postMessage('{"method":"play"}', '*');
          overlay.style.display = 'none';
        });
      } else {
        // Fallback: try to click the iframe
        iframe.click();
        overlay.style.display = 'none';
      }
    }
  });
  
  videoContainer.appendChild(overlay);
  
  // Auto-hide overlay after 3 seconds if video starts playing
  setTimeout(() => {
    const iframe = document.getElementById('daily-video-iframe');
    if (iframe && iframe.contentWindow) {
      try {
        iframe.contentWindow.postMessage('{"method":"getPaused"}', '*');
      } catch (e) {
        // Ignore cross-origin errors
      }
    }
  }, 3000);
}

// Helper: Get current day (prioritizes user selection over calculated day)
function getCurrentDay() {
  return (g_LastSelection != null && g_LastSelection != undefined && g_LastSelection != -1) 
    ? g_LastSelection 
    : resolveDay();
}

// Helper: Clear all form state (buttons and textarea)
function clearFormState(toggleButtons, textarea) {
  if (toggleButtons) {
    toggleButtons.forEach(button => {
      button.classList.remove('active');
    });
  }
  if (textarea) {
    textarea.value = '';
  }
}

const FEEDBACK_BUTTON_CATEGORIES = ['flavor', 'texture', 'story', 'craftsmanship', 'artistry', 'everything'];
const FEEDBACK_RATING_KEY_MAP = {
  flavor: 'flavor_rating',
  texture: 'texture_rating',
  story: 'story_rating',
  craftsmanship: 'craftsmanship_rating',
  artistry: 'artistry_rating',
  everything: 'everything_rating',
};

function getFeedbackStorageKey(day) {
  return `advent_feedback:${day}`;
}

function readDayFeedback(day) {
  try {
    const storedValue = localStorage.getItem(getFeedbackStorageKey(day));
    if (!storedValue) return null;
    return JSON.parse(storedValue);
  } catch (error) {
    console.error('Error reading feedback data for day', day, error);
    return null;
  }
}

function writeDayFeedback(day, data) {
  try {
    localStorage.setItem(getFeedbackStorageKey(day), JSON.stringify(data));
  } catch (error) {
    console.error('Error writing feedback data for day', day, error);
  }
}

function removeDayFeedback(day) {
  localStorage.removeItem(getFeedbackStorageKey(day));
}

function hasMeaningfulFeedbackValues(values) {
  if (!values) return false;
  const hasRating = Object.values(FEEDBACK_RATING_KEY_MAP).some(key => Number(values[key]) === 1);
  const hasComment = Boolean(values.comment && values.comment.trim().length > 0);
  return hasRating || hasComment;
}

function areFeedbackValuesEqual(a, b) {
  if (!a || !b) return false;
  for (const key of Object.values(FEEDBACK_RATING_KEY_MAP)) {
    if (Number(a[key] || 0) !== Number(b[key] || 0)) {
      return false;
    }
  }
  const aComment = (a.comment || '').trim();
  const bComment = (b.comment || '').trim();
  return aComment === bComment;
}

function buildDayFeedbackValues(selectedDay, buttonStates, comment) {
  const values = {
    day: selectedDay,
    comment: (comment || '').trim(),
  };
  FEEDBACK_BUTTON_CATEGORIES.forEach(category => {
    const ratingKey = FEEDBACK_RATING_KEY_MAP[category];
    values[ratingKey] = buttonStates && buttonStates[category] ? 1 : 0;
  });
  return values;
}

function getAllStoredFeedbackEntries() {
  const entries = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key || !key.startsWith('advent_feedback:')) continue;
    try {
      const parsed = JSON.parse(localStorage.getItem(key));
      if (parsed && typeof parsed.day !== 'undefined') {
        entries.push(parsed);
      }
    } catch (error) {
      console.error('Error parsing stored feedback entry', key, error);
    }
  }
  return entries;
}

function generateSubmissionId() {
  if (window.crypto && window.crypto.randomUUID) {
    return window.crypto.randomUUID();
  }
  return `submission-${Date.now()}-${Math.floor(Math.random() * 100000)}`;
}

// Generate UUID v4 format (fallback for browsers without crypto.randomUUID)
function generateUUIDv4() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });
}

// Get or generate persistent user_id from localStorage
// Returns existing user_id if present, generates new UUID v4 if missing
// Returns null if localStorage is unavailable
function getOrGenerateUserId() {
  const STORAGE_KEY = 'advent_user_id';
  
  try {
    // Try to get existing user_id
    const existingId = localStorage.getItem(STORAGE_KEY);
    if (existingId && typeof existingId === 'string' && existingId.trim().length > 0) {
      const trimmedId = existingId.trim();
      return trimmedId;
    }
    
    // Generate new UUID v4
    let newUserId;
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      newUserId = crypto.randomUUID();
    } else {
      // Fallback UUID v4 generation
      newUserId = generateUUIDv4();
    }
    
    // Store in localStorage
    localStorage.setItem(STORAGE_KEY, newUserId);
    return newUserId;
  } catch (error) {
    console.error('Error getting/generating user_id:', error);
    // Return null if localStorage unavailable
    return null;
  }
}

// Get Shopify customer ID from available global variables
// Returns null if customer is not logged in
function getShopifyCustomerId() {
  // Check window.Customer first (used in other parts of codebase)
  if (typeof window !== 'undefined' && window.Customer && window.Customer.id) {
    return window.Customer.id;
  }
  
  // Fallback to window.SHG_CUSTOMER (from shogun-head.liquid)
  if (typeof window !== 'undefined' && window.SHG_CUSTOMER && window.SHG_CUSTOMER.id) {
    return window.SHG_CUSTOMER.id;
  }
  
  // Fallback to Customer (local variable from init-client.liquid)
  if (typeof Customer !== 'undefined' && Customer && Customer.id) {
    return Customer.id;
  }
  
  // No customer ID found (user not logged in)
  return 'null';
}

function setFeedbackSubmitButtonState(label, disabled) {
  if (!g_feedbackSubmitButton) {
    g_feedbackSubmitButton = document.querySelector('.feedback-submit-button');
    if (!g_feedbackSubmitButton) {
      return;
    }
  }
  g_feedbackSubmitButton.textContent = label;
  g_feedbackSubmitButton.disabled = disabled;
}

function clearFeedbackButtonTimeouts() {
  if (g_feedbackSubmitButtonState.successTimeoutId) {
    clearTimeout(g_feedbackSubmitButtonState.successTimeoutId);
    g_feedbackSubmitButtonState.successTimeoutId = null;
  }
  if (g_feedbackSubmitButtonState.errorTimeoutId) {
    clearTimeout(g_feedbackSubmitButtonState.errorTimeoutId);
    g_feedbackSubmitButtonState.errorTimeoutId = null;
  }
  if (g_feedbackSubmitButtonState.errorRetryTimeoutId) {
    clearTimeout(g_feedbackSubmitButtonState.errorRetryTimeoutId);
    g_feedbackSubmitButtonState.errorRetryTimeoutId = null;
  }
}

function updateFeedbackSubmitButtonState(day, dayFeedback, options = {}) {
  if (!g_feedbackSubmitButton) {
    g_feedbackSubmitButton = document.querySelector('.feedback-submit-button');
    if (!g_feedbackSubmitButton) return;
  }
  if (typeof day === 'undefined' || day === null) {
    day = getCurrentDay();
  }
  g_feedbackSubmitButtonState.currentDay = day;

  if (options.forceState === 'loading') {
    clearFeedbackButtonTimeouts();
    g_feedbackSubmitButtonState.isSubmitting = true;
    setFeedbackSubmitButtonState('SUBMITTING…', true);
    return;
  }

  if (options.forceState === 'success') {
    clearFeedbackButtonTimeouts();
    g_feedbackSubmitButtonState.isSubmitting = false;
    setFeedbackSubmitButtonState('SUCCESS', true);
    g_feedbackSubmitButtonState.successTimeoutId = setTimeout(() => {
      g_feedbackSubmitButtonState.successTimeoutId = null;
      if (g_feedbackSubmitButtonState.currentDay !== day) return;
      const storedFeedback = readDayFeedback(day);
      updateFeedbackSubmitButtonState(day, storedFeedback);
    }, 2000);
    return;
  }

  if (options.forceState === 'error') {
    clearFeedbackButtonTimeouts();
    g_feedbackSubmitButtonState.isSubmitting = false;
    setFeedbackSubmitButtonState('SUBMIT FAILED…', true);
    g_feedbackSubmitButtonState.errorTimeoutId = setTimeout(() => {
      g_feedbackSubmitButtonState.errorTimeoutId = null;
      if (g_feedbackSubmitButtonState.currentDay !== day) return;
      const storedFeedback = readDayFeedback(day);
      const hasUnsyncedChanges = storedFeedback && storedFeedback.status && storedFeedback.status !== FEEDBACK_STATUS.NO_CHANGE;
      const label = hasUnsyncedChanges ? 'PLEASE TRY AGAIN' : 'UPDATE';
      setFeedbackSubmitButtonState(label, !hasUnsyncedChanges);
      
      // After 2 more seconds, transition from "PLEASE TRY AGAIN" back to "UPDATE"
      if (hasUnsyncedChanges) {
        g_feedbackSubmitButtonState.errorRetryTimeoutId = setTimeout(() => {
          g_feedbackSubmitButtonState.errorRetryTimeoutId = null;
          if (g_feedbackSubmitButtonState.currentDay !== day) return;
          const currentFeedback = readDayFeedback(day);
          const stillHasUnsyncedChanges = currentFeedback && currentFeedback.status && currentFeedback.status !== FEEDBACK_STATUS.NO_CHANGE;
          setFeedbackSubmitButtonState('UPDATE', !stillHasUnsyncedChanges);
        }, 2000);
      }
    }, 2000);
    return;
  }

  clearFeedbackButtonTimeouts();
  g_feedbackSubmitButtonState.isSubmitting = false;

  const status = dayFeedback
    ? (dayFeedback.status || FEEDBACK_STATUS.CREATED)
    : FEEDBACK_STATUS.NO_CHANGE;
  const hasData = hasMeaningfulFeedbackValues(dayFeedback);

  if (!dayFeedback) {
    setFeedbackSubmitButtonState('SUBMIT', true);
    return;
  }

  if (status === FEEDBACK_STATUS.CREATED) {
    setFeedbackSubmitButtonState('SUBMIT', false);
    return;
  }

  if (status === FEEDBACK_STATUS.MODIFIED) {
    setFeedbackSubmitButtonState('UPDATE', false);
    return;
  }

  // status === nochange
  if (hasData) {
    setFeedbackSubmitButtonState('UPDATE', true);
  } else {
    setFeedbackSubmitButtonState('SUBMIT', true);
  }
}

function refreshFeedbackSubmitButtonState(day) {
  const resolvedDay = typeof day === 'undefined' || day === null ? getCurrentDay() : day;
  const storedFeedback = readDayFeedback(resolvedDay);
  updateFeedbackSubmitButtonState(resolvedDay, storedFeedback);
}

function markFeedbackEntriesAsSynced(dayList) {
  if (!Array.isArray(dayList) || !dayList.length) return;
  const uniqueDays = [...new Set(dayList)];
  uniqueDays.forEach(day => {
    const storedFeedback = readDayFeedback(day);
    if (!storedFeedback) return;
    storedFeedback.status = FEEDBACK_STATUS.NO_CHANGE;
    writeDayFeedback(day, storedFeedback);
  });
}

// Get form state from DOM nodes (buttons and textarea)
// Automatically queries DOM for feedback form elements
// Requires day parameter to be passed in (cannot be determined from DOM)
// Returns formState object with day, buttons, and comment
function getFormState(day) {
  // Query DOM elements directly - no need to pass them as arguments
  const toggleButtons = document.querySelectorAll('.feedback-toggle-btn');
  const textarea = document.getElementById('feedback-textarea');
  const resolvedDay = (day != null && day != undefined) ? day : getCurrentDay();
  
  // Get button states
  const buttonStates = {};
  if (toggleButtons) {
    toggleButtons.forEach(button => {
      const category = button.getAttribute('data-category');
      const isActive = button.classList.contains('active');
      buttonStates[category] = isActive ? 1 : 0;
    });
  }
  
  // Get textarea value
  const comment = textarea ? (textarea.value || '') : '';
  
  // Return form state with day included (captured at time of state creation)
  // This ensures the correct day is saved even if user switches days before timeout fires
  return {
    day: resolvedDay, // Day when this form state was captured
    buttons: buttonStates,
    comment: comment
  };
}

// Update feedback status (currently logs to console, can be extended for UI updates later)
function updateFeedbackStatus(statusText) {
  console.log('Feedback status:', statusText);
}

// Send feedback data to Zapier webhook
// Uses x-www-form-urlencoded format to avoid CORS restrictions
// Uses no-cors mode, so response is opaque and cannot be read
// Returns a Promise that resolves if fetch succeeds (assumes success), rejects on network error
// Note: With no-cors, we cannot detect HTTP error status codes (400, 500, etc.)
// Only network-level errors (connection failures) will be caught
// Errors are handled non-blocking (logged but don't interrupt user experience)
function sendFeedbackToWebhook(payload) {
  if (!window.FEEDBACK_FORM_CATCH_HOOK) {
    console.warn('Feedback webhook URL not configured');
    return Promise.reject(new Error('Webhook URL not configured'));
  }

  const params = new URLSearchParams();
  params.append('payload', JSON.stringify(payload));

  return fetch(window.FEEDBACK_FORM_CATCH_HOOK, {
    method: 'POST',
    mode: 'no-cors',
    body: params
  })
  // With no-cors, if fetch resolves, assume success
  // We cannot check response.ok or response.status (they're always false/0)
  // Only network errors will be caught in .catch()
  .catch(error => {
    // Only network errors will be caught here
    // (connection failures, DNS resolution failures, etc.)
    console.error('Error sending feedback to webhook:', error);
    throw error;
  });
}

// Save current form state to localStorage before switching dates
// Prevents loss of unsaved feedback when user switches dates
function saveCurrentFormStateBeforeSwitch() {
  const currentDay = getCurrentDay();
  const capturedFormState = getFormState(currentDay);
  if (!capturedFormState) return;
  saveFeedback(capturedFormState);
}

// Save feedback to localStorage
// Saves immediately when called (no delay)
// Uses formState.day (captured at click time) to ensure correct day is saved even if UI has updated
function saveFeedback(formState) {
  if (!formState) return;

  const capturedDay = formState.day;
  const selectedDay = (capturedDay != null && capturedDay !== undefined)
    ? capturedDay
    : getCurrentDay();

  const buttonStates = formState.buttons || {};
  const comment = formState.comment || '';
  const newValues = buildDayFeedbackValues(selectedDay, buttonStates, comment);
  const hasData = hasMeaningfulFeedbackValues(newValues);

  const existingFeedback = readDayFeedback(selectedDay);

  if (!hasData) {
    if (existingFeedback) {
      removeDayFeedback(selectedDay);
      console.log('Feedback cleared for day', selectedDay);
    }
    updateFeedbackSubmitButtonState(selectedDay, null);
    return;
  }

  let nextStatus;
  let lastEdited = existingFeedback ? existingFeedback.lastEdited : null;
  if (!existingFeedback) {
    nextStatus = FEEDBACK_STATUS.CREATED;
    lastEdited = new Date().toISOString();
  } else {
    const valuesEqual = areFeedbackValuesEqual(existingFeedback, newValues);
    if (valuesEqual) {
      // Values haven't changed - preserve existing status (likely NO_CHANGE)
      nextStatus = existingFeedback.status || FEEDBACK_STATUS.NO_CHANGE;
      // Don't update lastEdited if values are equal
      lastEdited = existingFeedback.lastEdited || new Date().toISOString();
    } else {
      // Values have changed - update status based on previous status
      if (existingFeedback.status === FEEDBACK_STATUS.CREATED) {
        nextStatus = FEEDBACK_STATUS.CREATED;
      } else {
        nextStatus = FEEDBACK_STATUS.MODIFIED;
      }
      lastEdited = new Date().toISOString();
    }
  }

  if (!lastEdited) {
    lastEdited = new Date().toISOString();
  }

  const day_feedback = {
    ...newValues,
    status: nextStatus,
    lastEdited
  };

  writeDayFeedback(selectedDay, day_feedback);
  updateFeedbackSubmitButtonState(selectedDay, day_feedback);
  console.log('Feedback saved to localStorage for day', selectedDay, day_feedback);
}

// Submit all feedback to webhook
// Saves current DOM state to localStorage first, then submits all days' feedback
function submitFeedback() {
  // Save current form state before submitting (reuse existing function)
  saveCurrentFormStateBeforeSwitch();

  const entries = getAllStoredFeedbackEntries();
  const records = entries
    .filter(entry => entry && hasMeaningfulFeedbackValues(entry))
    .map(entry => {
      const normalizedStatus = entry.status || FEEDBACK_STATUS.CREATED;
      return {
        day: entry.day,
        status: normalizedStatus,
        lastEdited: entry.lastEdited,
        flavor_rating: entry.flavor_rating || 0,
        texture_rating: entry.texture_rating || 0,
        story_rating: entry.story_rating || 0,
        craftsmanship_rating: entry.craftsmanship_rating || 0,
        artistry_rating: entry.artistry_rating || 0,
        everything_rating: entry.everything_rating || 0,
        comment: entry.comment || ''
      };
    })
    .filter(record => record.status !== FEEDBACK_STATUS.NO_CHANGE);

  if (!records.length) {
    updateFeedbackStatus('No changes to submit');
    console.log('No feedback found in localStorage to submit');
    refreshFeedbackSubmitButtonState(getCurrentDay());
    return;
  }

  // for zapier to work, i need to have values for klaviyo_id and shopify_customer_id
  // i am using strings like 'null' instead of null to avoid errors
  // in the future, fix this when we have a backend system
  const urlParams = getUrlParameters();
  const userId = getOrGenerateUserId();
  const shopifyCustomerId = getShopifyCustomerId();
  const payload = {
    submission_id: generateSubmissionId(),
    submitted_at: new Date().toISOString(),
    records,
    url_parameters: urlParams,
    klaviyo_id: urlParams._kx || 'null',  // Explicitly extract Klaviyo ID
    user_id: userId,  // Persistent user ID for tracking across sessions
    shopify_customer_id: shopifyCustomerId  // Shopify customer ID if logged in
  };

  const submissionDay = getCurrentDay();
  const shouldUpdateActiveButton = () => g_feedbackSubmitButtonState.currentDay === submissionDay;

  if (shouldUpdateActiveButton()) {
    updateFeedbackSubmitButtonState(submissionDay, readDayFeedback(submissionDay), { forceState: 'loading' });
  }
  updateFeedbackStatus(`Submitting ${records.length} record(s)...`);

  let submissionCompleted = false;
  const submissionTimeout = setTimeout(() => {
    if (submissionCompleted) return;
    submissionCompleted = true;
    updateFeedbackStatus('Submission timed out. Please try again.');
    if (shouldUpdateActiveButton()) {
      updateFeedbackSubmitButtonState(submissionDay, readDayFeedback(submissionDay), { forceState: 'error' });
    } else {
      refreshFeedbackSubmitButtonState();
    }
  }, 3000);

  sendFeedbackToWebhook(payload)
    .then(() => {
      if (submissionCompleted) return;
      submissionCompleted = true;
      clearTimeout(submissionTimeout);
      markFeedbackEntriesAsSynced(records.map(record => record.day));
      updateFeedbackStatus(`Successfully submitted ${records.length} record(s)`);
      if (shouldUpdateActiveButton()) {
        updateFeedbackSubmitButtonState(submissionDay, readDayFeedback(submissionDay), { forceState: 'success' });
      } else {
        refreshFeedbackSubmitButtonState();
      }
    })
    .catch(error => {
      if (submissionCompleted) return;
      submissionCompleted = true;
      clearTimeout(submissionTimeout);
      console.error('Error during feedback submission process', error);
      updateFeedbackStatus(`Submission error: ${error.message || error}`);
      if (shouldUpdateActiveButton()) {
        updateFeedbackSubmitButtonState(submissionDay, readDayFeedback(submissionDay), { forceState: 'error' });
      } else {
        refreshFeedbackSubmitButtonState();
      }
    });
}

// Set all buttons to a specified state
// Called when the "everything" button is toggled to sync all buttons
function setAllButtonsState(toggleButtons, state) {
  toggleButtons.forEach(button => {
    if (state) {
      button.classList.add('active');
    } else {
      button.classList.remove('active');
    }
  });
}

// Load and restore form state from localStorage for the current day
// Queries DOM directly for form elements (consistent with getFormState)
function loadSavedFormState() {
  // Query DOM elements directly
  const toggleButtons = document.querySelectorAll('.feedback-toggle-btn');
  const textarea = document.getElementById('feedback-textarea');
  
  const selectedDay = getCurrentDay();
  
  // Load from localStorage
  const storageKey = `advent_feedback:${selectedDay}`;
  const savedData = localStorage.getItem(storageKey);
  
  if (!savedData) {
    // No saved data for this day - clear all buttons and textarea
    clearFormState(toggleButtons, textarea);
    console.log('No saved feedback found for day', selectedDay);
    updateFeedbackSubmitButtonState(selectedDay, null);
    return;
  }
  
  try {
    const day_feedback = JSON.parse(savedData);
    
    // Map rating fields to button categories
    const ratingMap = {
      flavor: day_feedback.flavor_rating || 0,
      texture: day_feedback.texture_rating || 0,
      story: day_feedback.story_rating || 0,
      craftsmanship: day_feedback.craftsmanship_rating || 0,
      artistry: day_feedback.artistry_rating || 0,
      everything: day_feedback.everything_rating || 0
    };
    
    // Restore button states based on saved ratings
    if (toggleButtons) {
      toggleButtons.forEach(button => {
        const category = button.getAttribute('data-category');
        const rating = ratingMap[category] || 0;
        
        if (rating === 1) {
          button.classList.add('active');
        } else {
          button.classList.remove('active');
        }
      });
    }
    
    // Restore textarea value
    if (textarea) {
      textarea.value = day_feedback.comment || '';
    }
    
    console.log('Loaded saved feedback for day', selectedDay, day_feedback);
    updateFeedbackSubmitButtonState(selectedDay, day_feedback);
  } catch (error) {
    console.error('Error parsing saved feedback data:', error);
    // Clear all buttons and textarea if parsing fails
    clearFormState(toggleButtons, textarea);
    updateFeedbackSubmitButtonState(selectedDay, null);
  }
}

// Write feedback form toggle buttons
// Main entry point for form logic. Will also check local storage for saved form states and restore them.
function writeFeedbackToggleButtons() {
  const toggleButtons = document.querySelectorAll('.feedback-toggle-btn');
  const textarea = document.getElementById('feedback-textarea');

  if (textarea) {
    textarea.addEventListener('input', function() {
      const selectedDay = getCurrentDay();
      const capturedFormState = getFormState(selectedDay);
      saveFeedback(capturedFormState);
    });
  }
  
  toggleButtons.forEach(button => {
    // Remove existing listener if it exists
    const existingHandler = g_feedbackButtonHandlers.get(button);
    if (existingHandler) {
      button.removeEventListener('click', existingHandler);
    }
    
    // Create new click handler
    // this will toggle buttons on/off 
    // if the everything button is selected, all buttons will be selected.
    const clickHandler = function() {
      // Capture the current day when button is clicked to ensure correct save even if user switches days
      // Use getCurrentDay() to match what the form is actually displaying (g_LastSelection)
      const selectedDay = getCurrentDay();
      
      const buttonText = this.textContent.trim().toLowerCase();
      const buttonCategory = this.getAttribute('data-category');
      const isEverything = buttonText === 'everything' || buttonCategory === 'everything';
      
      if (isEverything) {
        // Toggle the "everything" button first
        this.classList.toggle('active');
        
        // Get the state after toggle (true = on, false = off)
        const isActive = this.classList.contains('active');
        
        // Set all buttons to the same state
        setAllButtonsState(toggleButtons, isActive);
      } else {
        // For other buttons, just toggle normally
        this.classList.toggle('active');
      }
      
      // Capture form state immediately after toggle (before UI might update)
      // Include selectedDay in form state to ensure correct day is saved even if user switches days
      const capturedFormState = getFormState(selectedDay);
      
      // Save immediately to localStorage (no debounce)
      saveFeedback(capturedFormState);
    };
    
    // Add new listener and store reference
    button.addEventListener('click', clickHandler);
    g_feedbackButtonHandlers.set(button, clickHandler);
  });
  
  // Add event listener for submit button
  const submitButton = document.querySelector('.feedback-submit-button');
  if (submitButton) {
    g_feedbackSubmitButton = submitButton;
    
    // Remove existing listener if it exists
    if (g_feedbackSubmitButtonHandler) {
      submitButton.removeEventListener('click', g_feedbackSubmitButtonHandler);
    }
    
    // Create new handler
    g_feedbackSubmitButtonHandler = function(e) {
      e.preventDefault(); // Prevent default form submission if button is in a form
      submitFeedback();
    };
    
    // Add new listener
    submitButton.addEventListener('click', g_feedbackSubmitButtonHandler);
    console.log('Feedback submit button initialized');
  } else {
    console.warn('Feedback submit button not found');
  }
  
  // Load and restore saved form state for the current day
  // This also updates the button state based on the loaded data's status
  loadSavedFormState();
  
  console.log('Feedback toggle buttons initialized');
}

// Write feedback form character limit validation
function writeFeedbackForm() {
  // Try to find elements with retry logic
  const textarea = document.getElementById('feedback-textarea');
  const characterCount = document.querySelector('.character-count-display');
  
  // Clear any existing retry timeout before setting a new one
  if (g_feedbackFormRetryTimeout) {
    clearTimeout(g_feedbackFormRetryTimeout);
    g_feedbackFormRetryTimeout = null;
  }
  
  if (!textarea) {
    console.log('Feedback textarea not found - will retry');
    // Retry after a short delay in case section is loaded dynamically
    g_feedbackFormRetryTimeout = setTimeout(function() {
      writeFeedbackForm();
      g_feedbackFormRetryTimeout = null;
    }, 500);
    return;
  }
  
  if (!characterCount) {
    console.log('Character count display not found - will retry');
    g_feedbackFormRetryTimeout = setTimeout(function() {
      writeFeedbackForm();
      g_feedbackFormRetryTimeout = null;
    }, 500);
    return;
  }
  
  function updateCharacterCount() {
    const length = textarea.value.length;
    if (characterCount) {
      characterCount.textContent = length;
    }
    
    // Toggle error class if reaches or exceeds 2000 characters
    if (length >= 2000) {
      textarea.classList.add('error');
    } else {
      textarea.classList.remove('error');
    }
  }
  
  // Update on input, paste, and cut events
  textarea.addEventListener('input', updateCharacterCount);
  textarea.addEventListener('paste', function(e) {
    setTimeout(updateCharacterCount, 10); // Delay to ensure paste is complete
  });
  textarea.addEventListener('cut', function(e) {
    setTimeout(updateCharacterCount, 10); // Delay to ensure cut is complete
  });
  
  // Initialize count on page load
  updateCharacterCount();
  
  console.log('Feedback form initialized successfully, textarea length:', textarea.value.length);
}



